import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import JsBarcode from 'jsbarcode';

// ============================================================
// STYLES COMMUNS
// ============================================================
const styles = {
  page: {
    padding: '30px',
    maxWidth: '1400px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
    border: '1px solid #e8ecf1'
  },
  subTabBar: {
    display: 'flex',
    gap: '4px',
    marginBottom: '24px',
    borderBottom: '2px solid #e0e5ea',
    paddingBottom: '0'
  },
  subTabBtn: (active) => ({
    padding: '10px 28px',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: active ? '#1aabf9' : 'transparent',
    color: active ? '#fff' : '#6c757d',
    fontWeight: active ? '600' : '500',
    borderRadius: '6px 6px 0 0',
    fontSize: '14px',
    transition: 'all 0.2s',
    marginBottom: '-2px',
    borderBottom: active ? '2px solid #1aabf9' : '2px solid transparent'
  }),
  btnPrimary: {
    backgroundColor: '#1aabf9',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  btnSecondary: {
    backgroundColor: '#f8f9fa',
    color: '#495057',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  },
  btnDanger: {
    backgroundColor: '#fff5f5',
    color: '#dc3545',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    padding: '8px 14px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500'
  },
  input: {
    width: '100%',
    padding: '9px 12px',
    border: '1px solid #dee2e6',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#495057',
    marginBottom: '5px'
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1aabf9',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '14px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e8ecf1'
  },
  badge: (color, bg) => ({
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color,
    backgroundColor: bg
  })
};

// ============================================================
// COMPOSANT PRINCIPAL : GestionStock
// ============================================================
function GestionStock() {
  const [activeTab, setActiveTab] = useState('fournisseurs');

  const subTabs = [
    { id: 'fournisseurs', label: '🏭 Fournisseurs' },
    { id: 'marchandises', label: '📦 Marchandises' },
    { id: 'stock',        label: '🗂️ Stock'        }
  ];

  return (
    <div style={styles.page}>
      {/* En-tête */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '26px', color: '#1a2332', fontWeight: '700' }}>
          Gestion des Stocks
        </h1>
        <p style={{ margin: '4px 0 0', color: '#6c757d', fontSize: '14px' }}>
          Fournisseurs, marchandises, réceptions et mouvements de stock
        </p>
      </div>

      {/* Sous-onglets */}
      <div style={styles.subTabBar}>
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={styles.subTabBtn(activeTab === tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {activeTab === 'fournisseurs' && <SectionFournisseurs />}
      {activeTab === 'marchandises' && <SectionMarchandises />}
      {activeTab === 'stock'        && <SectionStock />}
    </div>
  );
}

// ============================================================
// SECTION FOURNISSEURS
// ============================================================
function SectionFournisseurs() {
  const [fournisseurs, setFournisseurs]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [searchTerm, setSearchTerm]       = useState('');
  const [filterActif, setFilterActif]     = useState('actif');

  // Modales
  const [showModal, setShowModal]         = useState(false);
  const [showDetail, setShowDetail]       = useState(false);
  const [editingF, setEditingF]           = useState(null);
  const [selectedF, setSelectedF]         = useState(null);
  const [articlesLies, setArticlesLies]   = useState([]);

  // Formulaire
  const emptyForm = {
    raison_sociale: '', adresse: '', ville: '', code_postal: '', pays: 'France',
    site_web: '', email_general: '', email_commandes: '', telephone: '',
    siret: '', tva_intracommunautaire: '',
    commercial_nom: '', commercial_telephone: '', commercial_email: '',
    reference_client: '', delai_livraison_jours: '', montant_minimum_commande: '',
    conditions_paiement: '', actif: true, notes: ''
  };
  const [form, setForm]     = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => { loadFournisseurs(); }, []);

  const loadFournisseurs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('stock_fournisseurs')
      .select('*')
      .order('raison_sociale', { ascending: true });
    if (error) console.error('Erreur chargement fournisseurs:', error);
    else setFournisseurs(data || []);
    setLoading(false);
  };

  const loadArticlesLies = async (fournisseurId) => {
    const { data } = await supabase
      .from('stock_articles')
      .select('id, reference, designation, unite_stock, actif')
      .eq('fournisseur_id', fournisseurId)
      .order('designation', { ascending: true });
    setArticlesLies(data || []);
  };

  const openAdd = () => {
    setEditingF(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (f) => {
    setEditingF(f);
    setForm({
      raison_sociale: f.raison_sociale || '',
      adresse: f.adresse || '', ville: f.ville || '',
      code_postal: f.code_postal || '', pays: f.pays || 'France',
      site_web: f.site_web || '',
      email_general: f.email_general || '',
      email_commandes: f.email_commandes || '',
      telephone: f.telephone || '',
      siret: f.siret || '',
      tva_intracommunautaire: f.tva_intracommunautaire || '',
      commercial_nom: f.commercial_nom || '',
      commercial_telephone: f.commercial_telephone || '',
      commercial_email: f.commercial_email || '',
      reference_client: f.reference_client || '',
      delai_livraison_jours: f.delai_livraison_jours != null ? String(f.delai_livraison_jours) : '',
      montant_minimum_commande: f.montant_minimum_commande != null ? String(f.montant_minimum_commande) : '',
      conditions_paiement: f.conditions_paiement || '',
      actif: f.actif,
      notes: f.notes || ''
    });
    setError('');
    setShowModal(true);
  };

  const openDetail = async (f) => {
    setSelectedF(f);
    setArticlesLies([]);
    setShowDetail(true);
    await loadArticlesLies(f.id);
  };

  const handleSave = async () => {
    if (!form.raison_sociale.trim()) {
      setError('La raison sociale est obligatoire.');
      return;
    }
    setSaving(true);
    setError('');

    const payload = {
      raison_sociale:            form.raison_sociale.trim(),
      adresse:                   form.adresse.trim() || null,
      ville:                     form.ville.trim() || null,
      code_postal:               form.code_postal.trim() || null,
      pays:                      form.pays.trim() || 'France',
      site_web:                  form.site_web.trim() || null,
      email_general:             form.email_general.trim() || null,
      email_commandes:           form.email_commandes.trim() || null,
      telephone:                 form.telephone.trim() || null,
      siret:                     form.siret.trim() || null,
      tva_intracommunautaire:    form.tva_intracommunautaire.trim() || null,
      commercial_nom:            form.commercial_nom.trim() || null,
      commercial_telephone:      form.commercial_telephone.trim() || null,
      commercial_email:          form.commercial_email.trim() || null,
      reference_client:          form.reference_client.trim() || null,
      delai_livraison_jours:     form.delai_livraison_jours !== '' ? parseInt(form.delai_livraison_jours, 10) : 0,
      montant_minimum_commande:  form.montant_minimum_commande !== '' ? parseFloat(form.montant_minimum_commande) : null,
      conditions_paiement:       form.conditions_paiement.trim() || null,
      actif:                     form.actif,
      notes:                     form.notes.trim() || null
    };

    const result = editingF
      ? await supabase.from('stock_fournisseurs').update(payload).eq('id', editingF.id)
      : await supabase.from('stock_fournisseurs').insert([payload]);

    if (result.error) {
      setError('Erreur : ' + result.error.message);
    } else {
      setShowModal(false);
      loadFournisseurs();
    }
    setSaving(false);
  };

  const handleToggleActif = async (f) => {
    await supabase
      .from('stock_fournisseurs')
      .update({ actif: !f.actif })
      .eq('id', f.id);
    loadFournisseurs();
  };

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // Filtrage
  const filtered = fournisseurs.filter(f => {
    const matchSearch = !searchTerm ||
      f.raison_sociale.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (f.commercial_nom && f.commercial_nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (f.ville && f.ville.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchActif =
      filterActif === 'tous' ||
      (filterActif === 'actif'   && f.actif) ||
      (filterActif === 'inactif' && !f.actif);
    return matchSearch && matchActif;
  });

  // ---- RENDU ----
  return (
    <div>
      {/* Barre d'actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {/* Recherche */}
          <input
            type="text"
            placeholder="Rechercher (nom, commercial, ville...)"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ ...styles.input, width: '280px' }}
          />
          {/* Filtre statut */}
          <select
            value={filterActif}
            onChange={e => setFilterActif(e.target.value)}
            style={{ ...styles.input, width: '150px' }}
          >
            <option value="actif">Actifs seulement</option>
            <option value="inactif">Inactifs</option>
            <option value="tous">Tous</option>
          </select>
        </div>
        <button style={styles.btnPrimary} onClick={openAdd}>
          + Ajouter un fournisseur
        </button>
      </div>

      {/* Compteur */}
      <p style={{ color: '#6c757d', fontSize: '13px', marginBottom: '16px' }}>
        {filtered.length} fournisseur{filtered.length > 1 ? 's' : ''}
        {filterActif !== 'tous' ? ` (${filterActif}s)` : ''}
      </p>

      {/* Tableau */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Chargement...</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...styles.card, textAlign: 'center', padding: '50px', color: '#6c757d' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🏭</div>
          <div style={{ fontWeight: '600', marginBottom: '6px' }}>Aucun fournisseur</div>
          <div style={{ fontSize: '13px' }}>Cliquez sur "Ajouter un fournisseur" pour commencer.</div>
        </div>
      ) : (
        <div style={styles.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e8ecf1' }}>
                  {['Fournisseur', 'Commercial', 'Tél. direct', 'Email commandes', 'Délai livraison', 'Statut', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#495057', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((f, i) => (
                  <tr key={f.id} style={{ borderBottom: '1px solid #f0f2f5', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: '600', color: '#1a2332' }}>{f.raison_sociale}</div>
                      {f.ville && <div style={{ fontSize: '12px', color: '#6c757d' }}>{f.ville}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#495057' }}>
                      {f.commercial_nom || <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#495057', whiteSpace: 'nowrap' }}>
                      {f.commercial_telephone
                        ? <a href={`tel:${f.commercial_telephone}`} style={{ color: '#1aabf9', textDecoration: 'none' }}>{f.commercial_telephone}</a>
                        : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {f.email_commandes
                        ? <a href={`mailto:${f.email_commandes}`} style={{ color: '#1aabf9', textDecoration: 'none', fontSize: '13px' }}>{f.email_commandes}</a>
                        : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                      {f.delai_livraison_jours > 0
                        ? <span style={{ fontWeight: '500', color: '#495057' }}>{f.delai_livraison_jours} j</span>
                        : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {f.actif
                        ? <span style={styles.badge('#1a7f4e', '#d4edda')}>Actif</span>
                        : <span style={styles.badge('#6c757d', '#e9ecef')}>Inactif</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => openDetail(f)}
                          style={{ ...styles.btnSecondary, padding: '6px 12px', fontSize: '12px' }}
                          title="Voir la fiche"
                        >
                          Voir
                        </button>
                        <button
                          onClick={() => openEdit(f)}
                          style={{ ...styles.btnSecondary, padding: '6px 12px', fontSize: '12px' }}
                          title="Modifier"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleToggleActif(f)}
                          style={{ ...styles.btnDanger, padding: '6px 12px', fontSize: '12px' }}
                          title={f.actif ? 'Désactiver' : 'Réactiver'}
                        >
                          {f.actif ? 'Désactiver' : 'Réactiver'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* MODAL : Créer / Modifier un fournisseur                             */}
      {/* ------------------------------------------------------------------ */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '14px',
            width: '100%', maxWidth: '780px', maxHeight: '90vh',
            overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }}>
            {/* Header modal */}
            <div style={{ padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1a2332' }}>
                {editingF ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6c757d' }}>×</button>
            </div>

            {/* Corps modal */}
            <div style={{ padding: '20px 28px 28px' }}>
              {error && (
                <div style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '12px 16px', color: '#dc3545', marginBottom: '20px', fontSize: '14px' }}>
                  {error}
                </div>
              )}

              {/* Section 1 : Identité */}
              <div style={{ marginBottom: '24px' }}>
                <div style={styles.sectionTitle}>Identité de l'entreprise</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Raison sociale <span style={{ color: '#dc3545' }}>*</span></label>
                    <input style={styles.input} value={form.raison_sociale} onChange={e => setField('raison_sociale', e.target.value)} placeholder="Ex: Textileo SAS" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Adresse</label>
                    <input style={styles.input} value={form.adresse} onChange={e => setField('adresse', e.target.value)} placeholder="N° et nom de rue" />
                  </div>
                  <div>
                    <label style={styles.label}>Ville</label>
                    <input style={styles.input} value={form.ville} onChange={e => setField('ville', e.target.value)} placeholder="Paris" />
                  </div>
                  <div>
                    <label style={styles.label}>Code postal</label>
                    <input style={styles.input} value={form.code_postal} onChange={e => setField('code_postal', e.target.value)} placeholder="75001" />
                  </div>
                  <div>
                    <label style={styles.label}>Pays</label>
                    <input style={styles.input} value={form.pays} onChange={e => setField('pays', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Site web</label>
                    <input style={styles.input} value={form.site_web} onChange={e => setField('site_web', e.target.value)} placeholder="https://..." />
                  </div>
                </div>
              </div>

              {/* Section 2 : Contacts entreprise */}
              <div style={{ marginBottom: '24px' }}>
                <div style={styles.sectionTitle}>Contacts entreprise</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={styles.label}>Téléphone standard</label>
                    <input style={styles.input} value={form.telephone} onChange={e => setField('telephone', e.target.value)} placeholder="01 23 45 67 89" />
                  </div>
                  <div>
                    <label style={styles.label}>Email général</label>
                    <input style={styles.input} type="email" value={form.email_general} onChange={e => setField('email_general', e.target.value)} placeholder="contact@fournisseur.fr" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Email commandes <span style={{ fontSize: '11px', color: '#6c757d' }}>(utilisé pour passer les commandes)</span></label>
                    <input style={styles.input} type="email" value={form.email_commandes} onChange={e => setField('email_commandes', e.target.value)} placeholder="commandes@fournisseur.fr" />
                  </div>
                  <div>
                    <label style={styles.label}>SIRET</label>
                    <input style={styles.input} value={form.siret} onChange={e => setField('siret', e.target.value)} placeholder="12345678900012" />
                  </div>
                  <div>
                    <label style={styles.label}>TVA intracommunautaire</label>
                    <input style={styles.input} value={form.tva_intracommunautaire} onChange={e => setField('tva_intracommunautaire', e.target.value)} placeholder="FR12345678901" />
                  </div>
                </div>
              </div>

              {/* Section 3 : Commercial dédié */}
              <div style={{ marginBottom: '24px' }}>
                <div style={styles.sectionTitle}>Commercial dédié</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Nom du commercial</label>
                    <input style={styles.input} value={form.commercial_nom} onChange={e => setField('commercial_nom', e.target.value)} placeholder="Prénom Nom" />
                  </div>
                  <div>
                    <label style={styles.label}>Téléphone direct</label>
                    <input style={styles.input} value={form.commercial_telephone} onChange={e => setField('commercial_telephone', e.target.value)} placeholder="06 12 34 56 78" />
                  </div>
                  <div>
                    <label style={styles.label}>Email direct</label>
                    <input style={styles.input} type="email" value={form.commercial_email} onChange={e => setField('commercial_email', e.target.value)} placeholder="prenom.nom@fournisseur.fr" />
                  </div>
                </div>
              </div>

              {/* Section 4 : Infos commerciales */}
              <div style={{ marginBottom: '24px' }}>
                <div style={styles.sectionTitle}>Informations commerciales</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={styles.label}>Votre n° client chez eux</label>
                    <input style={styles.input} value={form.reference_client} onChange={e => setField('reference_client', e.target.value)} placeholder="Ex: CLI-00847" />
                  </div>
                  <div>
                    <label style={styles.label}>Délai de livraison moyen (jours ouvrés)</label>
                    <input style={styles.input} type="number" min="0" value={form.delai_livraison_jours} onChange={e => setField('delai_livraison_jours', e.target.value)} placeholder="5" />
                  </div>
                  <div>
                    <label style={styles.label}>Montant minimum de commande (€ HT)</label>
                    <input style={styles.input} type="number" min="0" step="0.01" value={form.montant_minimum_commande} onChange={e => setField('montant_minimum_commande', e.target.value)} placeholder="150.00" />
                  </div>
                  <div>
                    <label style={styles.label}>Conditions de paiement</label>
                    <input style={styles.input} value={form.conditions_paiement} onChange={e => setField('conditions_paiement', e.target.value)} placeholder="Ex: 30j fin de mois" />
                  </div>
                </div>
              </div>

              {/* Section 5 : Notes + Statut */}
              <div style={{ marginBottom: '24px' }}>
                <div style={styles.sectionTitle}>Notes & Statut</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '14px', alignItems: 'start' }}>
                  <div>
                    <label style={styles.label}>Notes libres</label>
                    <textarea
                      style={{ ...styles.input, height: '80px', resize: 'vertical' }}
                      value={form.notes}
                      onChange={e => setField('notes', e.target.value)}
                      placeholder="Informations complémentaires..."
                    />
                  </div>
                  <div style={{ paddingTop: '22px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: '500', fontSize: '14px' }}>
                      <input
                        type="checkbox"
                        checked={form.actif}
                        onChange={e => setField('actif', e.target.checked)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      Fournisseur actif
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer modal */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '4px' }}>
                <button style={styles.btnSecondary} onClick={() => setShowModal(false)}>Annuler</button>
                <button style={styles.btnPrimary} onClick={handleSave} disabled={saving}>
                  {saving ? 'Enregistrement...' : (editingF ? 'Enregistrer les modifications' : 'Créer le fournisseur')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* MODAL : Fiche détail fournisseur                                    */}
      {/* ------------------------------------------------------------------ */}
      {showDetail && selectedF && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '14px',
            width: '100%', maxWidth: '680px', maxHeight: '90vh',
            overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }}>
            <div style={{ padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', color: '#1a2332' }}>{selectedF.raison_sociale}</h2>
                {selectedF.ville && <p style={{ margin: '4px 0 0', color: '#6c757d', fontSize: '13px' }}>{selectedF.ville}</p>}
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {selectedF.actif
                  ? <span style={styles.badge('#1a7f4e', '#d4edda')}>Actif</span>
                  : <span style={styles.badge('#6c757d', '#e9ecef')}>Inactif</span>}
                <button onClick={() => setShowDetail(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6c757d' }}>×</button>
              </div>
            </div>

            <div style={{ padding: '20px 28px 28px' }}>
              {/* Contacts */}
              {(selectedF.telephone || selectedF.email_general || selectedF.email_commandes || selectedF.site_web) && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={styles.sectionTitle}>Contacts entreprise</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', fontSize: '14px' }}>
                    {selectedF.telephone && <InfoRow label="Téléphone" value={<a href={`tel:${selectedF.telephone}`} style={{ color: '#1aabf9' }}>{selectedF.telephone}</a>} />}
                    {selectedF.email_general && <InfoRow label="Email général" value={<a href={`mailto:${selectedF.email_general}`} style={{ color: '#1aabf9' }}>{selectedF.email_general}</a>} />}
                    {selectedF.email_commandes && <InfoRow label="Email commandes" value={<a href={`mailto:${selectedF.email_commandes}`} style={{ color: '#1aabf9' }}>{selectedF.email_commandes}</a>} />}
                    {selectedF.site_web && <InfoRow label="Site web" value={<a href={selectedF.site_web} target="_blank" rel="noopener noreferrer" style={{ color: '#1aabf9' }}>{selectedF.site_web}</a>} />}
                  </div>
                </div>
              )}

              {/* Commercial */}
              {(selectedF.commercial_nom || selectedF.commercial_telephone || selectedF.commercial_email) && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={styles.sectionTitle}>Commercial dédié</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', fontSize: '14px' }}>
                    {selectedF.commercial_nom && <InfoRow label="Nom" value={selectedF.commercial_nom} />}
                    {selectedF.commercial_telephone && <InfoRow label="Tél. direct" value={<a href={`tel:${selectedF.commercial_telephone}`} style={{ color: '#1aabf9' }}>{selectedF.commercial_telephone}</a>} />}
                    {selectedF.commercial_email && <InfoRow label="Email direct" value={<a href={`mailto:${selectedF.commercial_email}`} style={{ color: '#1aabf9' }}>{selectedF.commercial_email}</a>} />}
                  </div>
                </div>
              )}

              {/* Infos commerciales */}
              {(selectedF.reference_client || selectedF.delai_livraison_jours > 0 || selectedF.montant_minimum_commande || selectedF.conditions_paiement) && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={styles.sectionTitle}>Informations commerciales</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px', fontSize: '14px' }}>
                    {selectedF.reference_client && <InfoRow label="N° client" value={selectedF.reference_client} />}
                    {selectedF.delai_livraison_jours > 0 && <InfoRow label="Délai livraison" value={`${selectedF.delai_livraison_jours} j. ouvrés`} />}
                    {selectedF.montant_minimum_commande && <InfoRow label="Minimum commande" value={`${parseFloat(selectedF.montant_minimum_commande).toFixed(2)} € HT`} />}
                    {selectedF.conditions_paiement && <InfoRow label="Conditions paiement" value={selectedF.conditions_paiement} />}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedF.notes && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={styles.sectionTitle}>Notes</div>
                  <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px 16px', fontSize: '14px', color: '#495057', whiteSpace: 'pre-wrap' }}>
                    {selectedF.notes}
                  </div>
                </div>
              )}

              {/* Articles liés */}
              <div style={{ marginBottom: '8px' }}>
                <div style={styles.sectionTitle}>Marchandises liées ({articlesLies.length})</div>
                {articlesLies.length === 0 ? (
                  <div style={{ color: '#6c757d', fontSize: '13px', fontStyle: 'italic' }}>
                    Aucune marchandise rattachée à ce fournisseur.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {articlesLies.map(a => (
                      <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '13px' }}>
                        <div>
                          <span style={{ fontWeight: '600', color: '#1a2332', marginRight: '8px' }}>{a.reference}</span>
                          <span style={{ color: '#495057' }}>{a.designation}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: '#6c757d' }}>{a.unite_stock}</span>
                          {!a.actif && <span style={styles.badge('#6c757d', '#e9ecef')}>Inactif</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions de la fiche */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e8ecf1' }}>
                <button style={styles.btnSecondary} onClick={() => setShowDetail(false)}>Fermer</button>
                <button style={styles.btnPrimary} onClick={() => { setShowDetail(false); openEdit(selectedF); }}>
                  Modifier ce fournisseur
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Petit composant utilitaire pour les lignes de la fiche détail
function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontWeight: '500', color: '#1a2332' }}>{value}</div>
    </div>
  );
}

// ============================================================
// SECTION MARCHANDISES (à développer — prochaine brique)
// ============================================================
// ============================================================
// SECTION MARCHANDISES — Catalogue des articles
// ============================================================
function SectionMarchandises() {
  const [articles, setArticles]         = useState([]);
  const [categories, setCategories]     = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading]           = useState(true);

  // Filtres
  const [search, setSearch]             = useState('');
  const [filterCat, setFilterCat]       = useState('');
  const [filterMode, setFilterMode]     = useState('');
  const [filterActif, setFilterActif]   = useState('actif');

  // Modales
  const [showArticleModal, setShowArticleModal]   = useState(false);
  const [showCatModal, setShowCatModal]           = useState(false);
  const [editingArticle, setEditingArticle]       = useState(null);
  const [saving, setSaving]                       = useState(false);
  const [error, setError]                         = useState('');

  // Formulaire article
  const emptyArticle = {
    reference: '', designation: '', description: '',
    categorie_id: '', fournisseur_id: '', reference_fournisseur: '', famille: '',
    mode_gestion: 'SORTIE',
    unite_stock: 'pièce', unite_consommation: '', contenance: '', laize_cm: '',
    contenance_variable: false, type_prix: 'PAR_UNITE_STOCK',
    seuil_alerte: '', quantite_cible: '', delai_appro_jours: '',
    prix_achat_ht: '', actif: true, notes: ''
  };
  const [formA, setFormA] = useState(emptyArticle);

  // Formulaire catégorie
  const [formCat, setFormCat]           = useState({ nom: '', description: '', couleur: '#6c757d' });
  const [editingCat, setEditingCat]     = useState(null);
  const [savingCat, setSavingCat]       = useState(false);
  const [errorCat, setErrorCat]         = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    const [artRes, catRes, fouRes] = await Promise.all([
      supabase.from('stock_articles')
        .select('*, stock_categories(nom, couleur), stock_fournisseurs(raison_sociale)')
        .order('designation', { ascending: true }),
      supabase.from('stock_categories')
        .select('*').eq('actif', true).order('ordre', { ascending: true }),
      supabase.from('stock_fournisseurs')
        .select('id, raison_sociale').eq('actif', true).order('raison_sociale')
    ]);
    if (!artRes.error) setArticles(artRes.data || []);
    if (!catRes.error) setCategories(catRes.data || []);
    if (!fouRes.error) setFournisseurs(fouRes.data || []);
    setLoading(false);
  };

  // -- Génération référence unique --
  const generateReference = () => {
    const chars = '0123456789ABCDEFGHJKLMNPRSTUVWXY';
    let code = 'MAT-';
    for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    return code;
  };

  // -- Ouvrir modale article --
  const openAdd = () => {
    setEditingArticle(null);
    setFormA({ ...emptyArticle, reference: generateReference() });
    setError('');
    setShowArticleModal(true);
  };

  const openEdit = (a) => {
    setEditingArticle(a);
    setFormA({
      reference: a.reference || '',
      designation: a.designation || '',
      description: a.description || '',
      categorie_id: a.categorie_id || '',
      fournisseur_id: a.fournisseur_id || '',
      reference_fournisseur: a.reference_fournisseur || '',
      famille: a.famille || '',
      mode_gestion: a.mode_gestion || 'SORTIE',
      unite_stock: a.unite_stock || 'pièce',
      unite_consommation: a.unite_consommation || '',
      contenance: a.contenance != null ? String(a.contenance) : '',
      laize_cm: a.laize_cm != null ? String(a.laize_cm) : '',
      contenance_variable: a.contenance_variable || false,
      type_prix: a.type_prix || 'PAR_UNITE_STOCK',
      seuil_alerte: a.seuil_alerte != null ? String(a.seuil_alerte) : '',
      quantite_cible: a.quantite_cible != null ? String(a.quantite_cible) : '',
      delai_appro_jours: a.delai_appro_jours != null ? String(a.delai_appro_jours) : '',
      prix_achat_ht: a.prix_achat_ht != null ? String(a.prix_achat_ht) : '',
      actif: a.actif,
      notes: a.notes || ''
    });
    setError('');
    setShowArticleModal(true);
  };

  const setA = (k, v) => setFormA(p => ({ ...p, [k]: v }));

  // -- Sauvegarder article --
  const handleSaveArticle = async () => {
    if (!formA.designation.trim()) { setError('La désignation est obligatoire.'); return; }
    if (!formA.reference.trim())   { setError('La référence est obligatoire.'); return; }
    if (formA.mode_gestion === 'SUIVI' && !formA.unite_consommation.trim()) {
      setError('L\'unité de consommation est obligatoire en mode SUIVI.'); return;
    }
    setSaving(true); setError('');

    const payload = {
      reference:             formA.reference.trim().toUpperCase(),
      designation:           formA.designation.trim(),
      description:           formA.description.trim() || null,
      categorie_id:          formA.categorie_id || null,
      fournisseur_id:        formA.fournisseur_id || null,
      reference_fournisseur: formA.reference_fournisseur.trim() || null,
      famille:               formA.famille.trim() || null,
      mode_gestion:          formA.mode_gestion,
      unite_stock:           formA.unite_stock.trim() || 'pièce',
      unite_consommation:    formA.mode_gestion === 'SUIVI' ? (formA.unite_consommation.trim() || null) : null,
      contenance:            formA.contenance !== '' ? parseFloat(formA.contenance) : null,
      laize_cm:              (formA.mode_gestion === 'SUIVI' && formA.laize_cm !== '') ? parseFloat(formA.laize_cm) : null,
      contenance_variable:   formA.mode_gestion === 'SUIVI' ? Boolean(formA.contenance_variable) : false,
      type_prix:             formA.type_prix || 'PAR_UNITE_STOCK',
      seuil_alerte:          formA.seuil_alerte !== '' ? parseInt(formA.seuil_alerte, 10) : 0,
      quantite_cible:        formA.quantite_cible !== '' ? parseInt(formA.quantite_cible, 10) : 0,
      delai_appro_jours:     formA.delai_appro_jours !== '' ? parseInt(formA.delai_appro_jours, 10) : 0,
      prix_achat_ht:         formA.prix_achat_ht !== '' ? parseFloat(formA.prix_achat_ht) : null,
      actif:                 formA.actif,
      notes:                 formA.notes.trim() || null
    };

    const result = editingArticle
      ? await supabase.from('stock_articles').update(payload).eq('id', editingArticle.id)
      : await supabase.from('stock_articles').insert([payload]);

    if (result.error) {
      setError(result.error.message.includes('unique') ? 'Cette référence existe déjà. Modifiez-la.' : 'Erreur : ' + result.error.message);
    } else {
      setShowArticleModal(false);
      loadAll();
    }
    setSaving(false);
  };

  const handleToggleActifArticle = async (a) => {
    await supabase.from('stock_articles').update({ actif: !a.actif }).eq('id', a.id);
    loadAll();
  };

  // -- Gestion catégories --
  const openCatModal = () => {
    setFormCat({ nom: '', description: '', couleur: '#6c757d' });
    setEditingCat(null);
    setErrorCat('');
    setShowCatModal(true);
  };

  const openEditCat = (c) => {
    setEditingCat(c);
    setFormCat({ nom: c.nom, description: c.description || '', couleur: c.couleur || '#6c757d' });
    setErrorCat('');
  };

  const handleSaveCat = async () => {
    if (!formCat.nom.trim()) { setErrorCat('Le nom est obligatoire.'); return; }
    setSavingCat(true); setErrorCat('');
    const payload = {
      nom: formCat.nom.trim(),
      description: formCat.description.trim() || null,
      couleur: formCat.couleur || '#6c757d'
    };
    const result = editingCat
      ? await supabase.from('stock_categories').update(payload).eq('id', editingCat.id)
      : await supabase.from('stock_categories').insert([payload]);
    if (result.error) {
      setErrorCat(result.error.message.includes('unique') ? 'Ce nom de catégorie existe déjà.' : 'Erreur : ' + result.error.message);
    } else {
      setEditingCat(null);
      setFormCat({ nom: '', description: '', couleur: '#6c757d' });
      loadAll();
    }
    setSavingCat(false);
  };

  const handleToggleActifCat = async (c) => {
    await supabase.from('stock_categories').update({ actif: !c.actif }).eq('id', c.id);
    loadAll();
  };

  // -- Couleurs prédéfinies pour catégories --
  const presetColors = [
    '#8B4513','#4169E1','#228B22','#1a1a1a','#FF8C00',
    '#9370DB','#DC143C','#20B2AA','#DAA520','#6c757d',
    '#1aabf9','#e91e63','#00bcd4','#ff5722','#607d8b'
  ];

  // -- Filtrage --
  const filtered = articles.filter(a => {
    const matchSearch = !search ||
      a.designation.toLowerCase().includes(search.toLowerCase()) ||
      a.reference.toLowerCase().includes(search.toLowerCase()) ||
      (a.famille && a.famille.toLowerCase().includes(search.toLowerCase()));
    const matchCat    = !filterCat  || a.categorie_id === filterCat;
    const matchMode   = !filterMode || a.mode_gestion === filterMode;
    const matchActif  =
      filterActif === 'tous' ||
      (filterActif === 'actif'   && a.actif) ||
      (filterActif === 'inactif' && !a.actif);
    return matchSearch && matchCat && matchMode && matchActif;
  });

  const modeBadge = (mode) => mode === 'SUIVI'
    ? <span style={styles.badge('#1a5276', '#d6eaf8')}>SUIVI</span>
    : <span style={styles.badge('#5d6d7e', '#eaecee')}>SORTIE</span>;

  // ---- RENDU ----
  return (
    <div>
      {/* Barre d'actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Rechercher (désignation, référence, famille...)"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...styles.input, width: '280px' }}
          />
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...styles.input, width: '160px' }}>
            <option value="">Toutes catégories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
          <select value={filterMode} onChange={e => setFilterMode(e.target.value)} style={{ ...styles.input, width: '140px' }}>
            <option value="">Tous modes</option>
            <option value="SUIVI">Mode SUIVI</option>
            <option value="SORTIE">Mode SORTIE</option>
          </select>
          <select value={filterActif} onChange={e => setFilterActif(e.target.value)} style={{ ...styles.input, width: '150px' }}>
            <option value="actif">Actifs seulement</option>
            <option value="inactif">Inactifs</option>
            <option value="tous">Tous</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={{ ...styles.btnSecondary, display: 'flex', alignItems: 'center', gap: '6px' }} onClick={openCatModal}>
            🏷️ Gérer les catégories
          </button>
          <button style={styles.btnPrimary} onClick={openAdd}>
            + Ajouter une marchandise
          </button>
        </div>
      </div>

      {/* Compteur */}
      <p style={{ color: '#6c757d', fontSize: '13px', marginBottom: '16px' }}>
        {filtered.length} article{filtered.length > 1 ? 's' : ''}
        {filterActif !== 'tous' ? ` (${filterActif}s)` : ''}
      </p>

      {/* Tableau */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Chargement...</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...styles.card, textAlign: 'center', padding: '50px', color: '#6c757d' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📦</div>
          <div style={{ fontWeight: '600', marginBottom: '6px' }}>Aucune marchandise</div>
          <div style={{ fontSize: '13px' }}>Cliquez sur "Ajouter une marchandise" pour créer votre premier article.</div>
        </div>
      ) : (
        <div style={styles.card}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e8ecf1' }}>
                  {['Référence', 'Désignation', 'Catégorie', 'Mode', 'Unité stock', 'Fournisseur', 'Seuil alerte', 'Statut', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#495057', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #f0f2f5', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '12px 14px' }}>
                      <code style={{ backgroundColor: '#f0f2f5', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', color: '#1a2332' }}>
                        {a.reference}
                      </code>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontWeight: '600', color: '#1a2332' }}>{a.designation}</div>
                      {a.famille && <div style={{ fontSize: '11px', color: '#6c757d' }}>Famille : {a.famille}</div>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {a.stock_categories ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: a.stock_categories.couleur, display: 'inline-block' }} />
                          <span style={{ fontSize: '13px', color: '#495057' }}>{a.stock_categories.nom}</span>
                        </span>
                      ) : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>{modeBadge(a.mode_gestion)}</td>
                    <td style={{ padding: '12px 14px', color: '#495057' }}>
                      {a.unite_stock}
                      {a.mode_gestion === 'SUIVI' && a.contenance && (
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>
                          {a.contenance} {a.unite_consommation}{a.laize_cm ? ` · L${a.laize_cm}cm` : ''}
                        </div>
                      )}
                      {a.mode_gestion === 'SORTIE' && a.contenance && (
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>{a.contenance} unités</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '13px', color: '#495057' }}>
                      {a.stock_fournisseurs ? a.stock_fournisseurs.raison_sociale : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                      {a.seuil_alerte > 0
                        ? <span style={{ fontWeight: '500', color: '#495057' }}>{a.seuil_alerte}</span>
                        : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      {a.actif
                        ? <span style={styles.badge('#1a7f4e', '#d4edda')}>Actif</span>
                        : <span style={styles.badge('#6c757d', '#e9ecef')}>Inactif</span>}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => openEdit(a)} style={{ ...styles.btnSecondary, padding: '6px 12px', fontSize: '12px' }}>Modifier</button>
                        <button onClick={() => handleToggleActifArticle(a)} style={{ ...styles.btnDanger, padding: '6px 12px', fontSize: '12px' }}>
                          {a.actif ? 'Désactiver' : 'Réactiver'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* MODAL : Créer / Modifier un article                                 */}
      {/* ------------------------------------------------------------------ */}
      {showArticleModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '820px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1, borderBottom: '1px solid #e8ecf1', paddingBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1a2332' }}>
                {editingArticle ? 'Modifier la marchandise' : 'Nouvelle marchandise'}
              </h2>
              <button onClick={() => setShowArticleModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6c757d' }}>×</button>
            </div>

            <div style={{ padding: '20px 28px 28px' }}>
              {error && (
                <div style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '12px 16px', color: '#dc3545', marginBottom: '20px', fontSize: '14px' }}>
                  {error}
                </div>
              )}

              {/* Identité */}
              <div style={{ marginBottom: '22px' }}>
                <div style={styles.sectionTitle}>Identité</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '14px' }}>
                  <div>
                    <label style={styles.label}>Référence <span style={{ color: '#dc3545' }}>*</span></label>
                    <input style={{ ...styles.input, fontFamily: 'monospace', fontWeight: '600' }} value={formA.reference} onChange={e => setA('reference', e.target.value.toUpperCase())} placeholder="MAT-XXXXXX" />
                  </div>
                  <div>
                    <label style={styles.label}>Désignation <span style={{ color: '#dc3545' }}>*</span></label>
                    <input style={styles.input} value={formA.designation} onChange={e => setA('designation', e.target.value)} placeholder="Ex: Papier sublimation 120cm" />
                  </div>
                  <div>
                    <label style={styles.label}>Catégorie</label>
                    <select style={styles.input} value={formA.categorie_id} onChange={e => setA('categorie_id', e.target.value)}>
                      <option value="">— Aucune —</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Famille <span style={{ fontSize: '11px', color: '#6c757d' }}>(regroupement variantes)</span></label>
                    <input style={styles.input} value={formA.famille} onChange={e => setA('famille', e.target.value)} placeholder="Ex: T-shirt Gildan 64000" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Description</label>
                    <textarea style={{ ...styles.input, height: '60px', resize: 'vertical' }} value={formA.description} onChange={e => setA('description', e.target.value)} placeholder="Détails supplémentaires..." />
                  </div>
                </div>
              </div>

              {/* Fournisseur */}
              <div style={{ marginBottom: '22px' }}>
                <div style={styles.sectionTitle}>Fournisseur</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={styles.label}>Fournisseur principal</label>
                    <select style={styles.input} value={formA.fournisseur_id} onChange={e => setA('fournisseur_id', e.target.value)}>
                      <option value="">— Aucun —</option>
                      {fournisseurs.map(f => <option key={f.id} value={f.id}>{f.raison_sociale}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Référence chez le fournisseur</label>
                    <input style={styles.input} value={formA.reference_fournisseur} onChange={e => setA('reference_fournisseur', e.target.value)} placeholder="Leur code article" />
                  </div>
                </div>
              </div>

              {/* Mode de gestion */}
              <div style={{ marginBottom: '22px' }}>
                <div style={styles.sectionTitle}>Mode de gestion & Unités</div>
                {/* Sélecteur de mode */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  {[
                    { val: 'SORTIE', label: 'Mode SORTIE', desc: 'Un scan = sorti du stock. Boîtes, sachets, pièces, cartouches...' },
                    { val: 'SUIVI',  label: 'Mode SUIVI',  desc: 'Consommation partielle. Rouleaux, bidons, spools...' }
                  ].map(m => (
                    <div
                      key={m.val}
                      onClick={() => setA('mode_gestion', m.val)}
                      style={{
                        flex: 1, padding: '14px 16px', borderRadius: '10px', cursor: 'pointer',
                        border: formA.mode_gestion === m.val ? '2px solid #1aabf9' : '2px solid #e8ecf1',
                        backgroundColor: formA.mode_gestion === m.val ? '#f0f8ff' : '#fafbfc',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ fontWeight: '700', fontSize: '14px', color: formA.mode_gestion === m.val ? '#1aabf9' : '#495057', marginBottom: '4px' }}>{m.label}</div>
                      <div style={{ fontSize: '12px', color: '#6c757d', lineHeight: '1.4' }}>{m.desc}</div>
                    </div>
                  ))}
                </div>

                {/* Champs unités communs */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={styles.label}>Unité de stock <span style={{ color: '#dc3545' }}>*</span></label>
                    <input style={styles.input} value={formA.unite_stock} onChange={e => setA('unite_stock', e.target.value)} placeholder="rouleau, sachet, pièce..." />
                  </div>

                  {/* Champs SUIVI uniquement */}
                  {formA.mode_gestion === 'SUIVI' && (
                    <>
                      <div>
                        <label style={styles.label}>Unité de consommation <span style={{ color: '#dc3545' }}>*</span></label>
                        <input style={styles.input} value={formA.unite_consommation} onChange={e => setA('unite_consommation', e.target.value)} placeholder="m, mL, g, cm..." />
                      </div>
                      <div>
                        <label style={styles.label}>Contenance <span style={{ fontSize: '11px', color: '#6c757d' }}>(qté par {formA.unite_stock || 'unité'})</span></label>
                        <input style={styles.input} type="number" min="0" step="0.001" value={formA.contenance} onChange={e => setA('contenance', e.target.value)} placeholder={`Ex: 120 ${formA.unite_consommation || ''}`} />
                      </div>
                      <div>
                        <label style={styles.label}>Laize / Largeur (cm) <span style={{ fontSize: '11px', color: '#6c757d' }}>(optionnel)</span></label>
                        <input style={styles.input} type="number" min="0" step="0.1" value={formA.laize_cm} onChange={e => setA('laize_cm', e.target.value)} placeholder="Ex: 160" />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={styles.label}>Contenance : fixe ou variable ?</label>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                          {[{ val: false, label: '📐 Fixe', desc: `Toujours la même longueur (ex : 120 m par rouleau)` },
                            { val: true,  label: '📏 Variable', desc: 'Varie à la réception (ex : tissu, longueur différente à chaque livraison)' }
                          ].map(opt => (
                            <div key={String(opt.val)} onClick={() => setA('contenance_variable', opt.val)}
                              style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                                border: formA.contenance_variable === opt.val ? '2px solid #1aabf9' : '2px solid #e8ecf1',
                                backgroundColor: formA.contenance_variable === opt.val ? '#f0f8ff' : '#fafbfc', transition: 'all 0.15s' }}>
                              <div style={{ fontWeight: '700', fontSize: '13px', color: formA.contenance_variable === opt.val ? '#1aabf9' : '#495057' }}>{opt.label}</div>
                              <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '2px' }}>{opt.desc}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Contenance SORTIE (optionnel) */}
                  {formA.mode_gestion === 'SORTIE' && (
                    <div>
                      <label style={styles.label}>Contenance <span style={{ fontSize: '11px', color: '#6c757d' }}>(optionnel — ex: 1 boîte = 10 aiguilles)</span></label>
                      <input style={styles.input} type="number" min="0" step="1" value={formA.contenance} onChange={e => setA('contenance', e.target.value)} placeholder="Ex: 10" />
                    </div>
                  )}
                </div>
              </div>

              {/* Seuils & Appro */}
              <div style={{ marginBottom: '22px' }}>
                <div style={styles.sectionTitle}>Seuils & Approvisionnement</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={styles.label}>Seuil d'alerte <span style={{ fontSize: '11px', color: '#6c757d' }}>({formA.unite_stock || 'unités'})</span></label>
                    <input style={styles.input} type="number" min="0" value={formA.seuil_alerte} onChange={e => setA('seuil_alerte', e.target.value)} placeholder="0" />
                  </div>
                  <div>
                    <label style={styles.label}>Qté cible <span style={{ fontSize: '11px', color: '#6c757d' }}>(objectif)</span></label>
                    <input style={styles.input} type="number" min="0" value={formA.quantite_cible} onChange={e => setA('quantite_cible', e.target.value)} placeholder="0" />
                  </div>
                  <div>
                    <label style={styles.label}>Délai d'appro <span style={{ fontSize: '11px', color: '#6c757d' }}>(jours ouvrés)</span></label>
                    <input style={styles.input} type="number" min="0" value={formA.delai_appro_jours} onChange={e => setA('delai_appro_jours', e.target.value)} placeholder="0" />
                  </div>
                  <div>
                    <label style={styles.label}>Prix d'achat HT (€)</label>
                    <input style={styles.input} type="number" min="0" step="0.0001" value={formA.prix_achat_ht} onChange={e => setA('prix_achat_ht', e.target.value)} placeholder="0.00" />
                    <div style={{ marginTop: '8px' }}>
                      <label style={{ ...styles.label, marginBottom: '4px' }}>Référence du prix</label>
                      <select style={styles.input} value={formA.type_prix} onChange={e => setA('type_prix', e.target.value)}>
                        <option value="PAR_UNITE_STOCK">Par unité de stock ({formA.unite_stock || 'rouleau, sachet...'})</option>
                        <option value="PAR_UNITE_CONSO">Par unité de conso ({formA.unite_consommation || 'm, mL...'})</option>
                      </select>
                      <div style={{ fontSize: '10px', color: '#6c757d', marginTop: '3px' }}>
                        {formA.type_prix === 'PAR_UNITE_STOCK'
                          ? `Coût BOM = (qté / contenance) × prix${formA.contenance ? ` → 1 pièce = ${formA.prix_achat_ht ? (parseFloat(formA.prix_achat_ht)/parseFloat(formA.contenance||1)).toFixed(4) : '?'}₠€/${formA.unite_consommation || 'unité'}` : ''}`
                          : `Coût BOM = qté × prix → direct en ${formA.unite_consommation || formA.unite_stock}`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes & statut */}
              <div style={{ marginBottom: '22px' }}>
                <div style={styles.sectionTitle}>Notes & Statut</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '14px', alignItems: 'start' }}>
                  <div>
                    <label style={styles.label}>Notes</label>
                    <textarea style={{ ...styles.input, height: '70px', resize: 'vertical' }} value={formA.notes} onChange={e => setA('notes', e.target.value)} placeholder="Informations complémentaires..." />
                  </div>
                  <div style={{ paddingTop: '22px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: '500', fontSize: '14px' }}>
                      <input type="checkbox" checked={formA.actif} onChange={e => setA('actif', e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                      Article actif
                    </label>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '4px' }}>
                <button style={styles.btnSecondary} onClick={() => setShowArticleModal(false)}>Annuler</button>
                <button style={styles.btnPrimary} onClick={handleSaveArticle} disabled={saving}>
                  {saving ? 'Enregistrement...' : (editingArticle ? 'Enregistrer les modifications' : 'Créer la marchandise')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* MODAL : Gestion des catégories                                      */}
      {/* ------------------------------------------------------------------ */}
      {showCatModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '600px', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1a2332' }}>🏷️ Gérer les catégories</h2>
              <button onClick={() => { setShowCatModal(false); setEditingCat(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6c757d' }}>×</button>
            </div>

            <div style={{ padding: '20px 28px 28px' }}>
              {/* Formulaire ajout / modif */}
              <div style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px', marginBottom: '24px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#495057', marginBottom: '12px' }}>
                  {editingCat ? `Modifier : ${editingCat.nom}` : 'Nouvelle catégorie'}
                </div>
                {errorCat && (
                  <div style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '10px 14px', color: '#dc3545', marginBottom: '12px', fontSize: '13px' }}>
                    {errorCat}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={styles.label}>Nom <span style={{ color: '#dc3545' }}>*</span></label>
                    <input style={styles.input} value={formCat.nom} onChange={e => setFormCat(p => ({ ...p, nom: e.target.value }))} placeholder="Ex: Matière première" />
                  </div>
                  <div>
                    <label style={styles.label}>Description</label>
                    <input style={styles.input} value={formCat.description} onChange={e => setFormCat(p => ({ ...p, description: e.target.value }))} placeholder="Brève description" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Couleur</label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {presetColors.map(c => (
                        <div
                          key={c}
                          onClick={() => setFormCat(p => ({ ...p, couleur: c }))}
                          style={{
                            width: '26px', height: '26px', borderRadius: '50%', backgroundColor: c,
                            cursor: 'pointer', border: formCat.couleur === c ? '3px solid #1aabf9' : '3px solid transparent',
                            boxSizing: 'border-box', transition: 'border 0.15s'
                          }}
                        />
                      ))}
                      <input
                        type="text"
                        value={formCat.couleur}
                        onChange={e => setFormCat(p => ({ ...p, couleur: e.target.value }))}
                        style={{ ...styles.input, width: '100px', fontFamily: 'monospace' }}
                        placeholder="#6c757d"
                      />
                      <div style={{ width: '26px', height: '26px', borderRadius: '6px', backgroundColor: formCat.couleur, border: '1px solid #dee2e6' }} />
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
                  {editingCat && (
                    <button style={styles.btnSecondary} onClick={() => { setEditingCat(null); setFormCat({ nom: '', description: '', couleur: '#6c757d' }); setErrorCat(''); }}>
                      Annuler la modification
                    </button>
                  )}
                  <button style={styles.btnPrimary} onClick={handleSaveCat} disabled={savingCat}>
                    {savingCat ? 'Enregistrement...' : (editingCat ? 'Modifier' : '+ Créer la catégorie')}
                  </button>
                </div>
              </div>

              {/* Liste des catégories */}
              <div style={styles.sectionTitle}>Catégories existantes ({categories.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {categories.map(c => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: editingCat?.id === c.id ? '#f0f8ff' : '#f8f9fa', borderRadius: '8px', border: editingCat?.id === c.id ? '1px solid #1aabf9' : '1px solid transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: c.couleur, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a2332' }}>{c.nom}</div>
                        {c.description && <div style={{ fontSize: '12px', color: '#6c757d' }}>{c.description}</div>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => openEditCat(c)} style={{ ...styles.btnSecondary, padding: '5px 12px', fontSize: '12px' }}>Modifier</button>
                      <button onClick={() => handleToggleActifCat(c)} style={{ ...styles.btnDanger, padding: '5px 12px', fontSize: '12px' }}>
                        {c.actif ? 'Désactiver' : 'Réactiver'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button style={styles.btnSecondary} onClick={() => { setShowCatModal(false); setEditingCat(null); }}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// SECTION STOCK (à développer — prochaine brique)
// ============================================================
// ============================================================
// SECTION STOCK — Vue d'ensemble, réceptions, atelier, mouvements
// ============================================================
function SectionStock() {
  const [activeView, setActiveView] = useState('vue_stock');

  // Données
  const [stockData, setStockData]       = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [articles, setArticles]         = useState([]);
  const [receptions, setReceptions]     = useState([]);
  const [itemsAtelier, setItemsAtelier] = useState([]);
  const [mouvements, setMouvements]     = useState([]);
  const [loading, setLoading]           = useState(true);

  // KPIs
  const [kpis, setKpis] = useState({ ruptures: 0, sousSeuilOu: 0, enAtelier: 0, receptionsMonth: 0 });

  // Filtres vue stock
  const [searchStock, setSearchStock]         = useState('');
  const [filterStatutStock, setFilterStatutStock] = useState('tous');

  // ---- Modal BL ----
  const [showBLModal, setShowBLModal]   = useState(false);
  const [blForm, setBlForm]             = useState({ fournisseur_id: '', date_reception: todayStr(), numero_bl: '', numero_facture: '', notes: '' });
  const [blLignes, setBlLignes]         = useState([{ article_id: '', quantite_recue: 1, prix_unitaire_ht: '', quantites_par_item: [] }]);
  const [savingBL, setSavingBL]         = useState(false);
  const [errorBL, setErrorBL]           = useState('');

  // ---- Modal Scan ----
  const [showScanModal, setShowScanModal]   = useState(false);
  const [scanInput, setScanInput]           = useState('');
  const [scannedItem, setScannedItem]       = useState(null);  // { item, article }
  const [scanQte, setScanQte]               = useState('');
  const [scanNotes, setScanNotes]           = useState('');
  const [scanProcessing, setScanProcessing] = useState(false);
  const [scanError, setScanError]           = useState('');
  const [scanSuccess, setScanSuccess]       = useState('');
  const scanInputRef = useRef(null);

  // ---- Modal action atelier ----
  const [showAtelierModal, setShowAtelierModal]       = useState(false);
  const [selectedItemAtelier, setSelectedItemAtelier] = useState(null);
  const [atelierQte, setAtelierQte]                   = useState('');
  const [atelierNotes, setAtelierNotes]               = useState('');
  const [atelierProcessing, setAtelierProcessing]     = useState(false);
  const [atelierError, setAtelierError]               = useState('');

  // ---- Modal détail article ----
  const [showDetailModal, setShowDetailModal]           = useState(false);
  const [selectedArticleDetail, setSelectedArticleDetail] = useState(null);
  const [articleItems, setArticleItems]                 = useState([]);
  const [loadingItems, setLoadingItems]                 = useState(false);
  const [filterItemStatut, setFilterItemStatut]         = useState('actifs');

  // ---- Modal ajustement ----
  const [showAjustModal, setShowAjustModal]     = useState(false);
  const [selectedArticleAjust, setSelectedArticleAjust] = useState(null);
  const [ajustMode, setAjustMode]               = useState('add');  // 'add' | 'remove'
  const [ajustQte, setAjustQte]                 = useState('');
  const [ajustQteInitiale, setAjustQteInitiale] = useState('');
  const [ajustMotif, setAjustMotif]             = useState('');
  const [ajustProcessing, setAjustProcessing]   = useState(false);
  const [ajustError, setAjustError]             = useState('');

  function todayStr() { return new Date().toISOString().split('T')[0]; }

  const getOperateurId = () => {
    try { return JSON.parse(localStorage.getItem('operateur'))?.id || null; } catch { return null; }
  };

  const generateCodeBarreItem = () => {
    const chars = '0123456789BCDEFGHJKLNPRSTUVXY';
    let code = 'STK';
    for (let i = 0; i < 9; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    return code;
  };

  // ---- Chargements ----
  useEffect(() => { loadBase(); }, []);
  useEffect(() => {
    if (activeView === 'receptions')  loadReceptions();
    if (activeView === 'en_atelier')  loadItemsAtelier();
    if (activeView === 'mouvements')  loadMouvements();
  }, [activeView]);

  const loadBase = async () => {
    setLoading(true);
    const [stockRes, fouRes, artRes, atelierRes, receptionsMonthRes] = await Promise.all([
      supabase.rpc ? // utilise la vue si disponible, sinon fallback manuel
        supabase.from('v_stock_courant').select('*').order('designation') :
        supabase.from('stock_articles').select('*'),
      supabase.from('stock_fournisseurs').select('id, raison_sociale').eq('actif', true).order('raison_sociale'),
      supabase.from('stock_articles').select('id, reference, designation, mode_gestion, unite_stock, unite_consommation, contenance, laize_cm, seuil_alerte, fournisseur_id, contenance_variable, type_prix, prix_achat_ht').eq('actif', true).order('designation'),
      supabase.from('stock_items').select('id').eq('statut', 'en_atelier'),
      supabase.from('stock_receptions').select('id').gte('date_reception', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0])
    ]);

    const stock = stockRes.data || [];
    setStockData(stock);
    setFournisseurs(fouRes.data || []);
    setArticles(artRes.data || []);

    const ruptures  = stock.filter(s => (s.items_en_stock || 0) === 0).length;
    const sousSeuil = stock.filter(s => s.seuil_alerte > 0 && (s.items_en_stock || 0) > 0 && (s.items_en_stock || 0) <= s.seuil_alerte).length;
    setKpis({
      ruptures,
      sousSeuilOu: sousSeuil,
      enAtelier: (atelierRes.data || []).length,
      receptionsMonth: (receptionsMonthRes.data || []).length
    });
    setLoading(false);
  };

  const loadReceptions = async () => {
    const { data } = await supabase
      .from('stock_receptions')
      .select('*, stock_fournisseurs(raison_sociale), stock_reception_lignes(id, quantite_recue, stock_articles(designation))')
      .order('date_reception', { ascending: false })
      .limit(50);
    setReceptions(data || []);
  };

  const loadItemsAtelier = async () => {
    const { data } = await supabase
      .from('stock_items')
      .select('*, stock_articles(designation, reference, unite_consommation, contenance, mode_gestion)')
      .eq('statut', 'en_atelier')
      .order('date_sortie_stock', { ascending: true });
    setItemsAtelier(data || []);
  };

  const loadMouvements = async () => {
    const { data } = await supabase
      .from('stock_mouvements')
      .select('*, stock_items(code_barre), stock_articles(designation, reference), operateurs(prenom, nom)')
      .order('created_at', { ascending: false })
      .limit(100);
    setMouvements(data || []);
  };

  // ---- Créer le bon de réception ----
  const handleCreateBL = async () => {
    if (!blForm.fournisseur_id) { setErrorBL('Sélectionnez un fournisseur.'); return; }
    const lignesValides = blLignes.filter(l => l.article_id && l.quantite_recue > 0);
    if (lignesValides.length === 0) { setErrorBL('Ajoutez au moins une ligne valide.'); return; }

    setSavingBL(true); setErrorBL('');

    // 1. Créer l'en-tête de réception
    const { data: reception, error: errRec } = await supabase
      .from('stock_receptions')
      .insert([{
        fournisseur_id:  blForm.fournisseur_id,
        date_reception:  blForm.date_reception || todayStr(),
        numero_bl:       blForm.numero_bl.trim() || null,
        numero_facture:  blForm.numero_facture.trim() || null,
        notes:           blForm.notes.trim() || null,
        operateur_id:    getOperateurId()
      }])
      .select().single();

    if (errRec) { setErrorBL('Erreur création BL : ' + errRec.message); setSavingBL(false); return; }

    const allCreatedItems = [];

    // 2. Pour chaque ligne : créer la ligne + les items
    for (const ligne of lignesValides) {
      const art = articles.find(a => a.id === ligne.article_id);
      if (!art) continue;

      const { data: rl, error: errLigne } = await supabase
        .from('stock_reception_lignes')
        .insert([{
          reception_id:     reception.id,
          article_id:       ligne.article_id,
          quantite_recue:   parseInt(ligne.quantite_recue, 10),
          prix_unitaire_ht: ligne.prix_unitaire_ht !== '' ? parseFloat(ligne.prix_unitaire_ht) : null
        }])
        .select().single();

      if (errLigne) continue;

      // 3. Générer N items physiques
      for (let i = 0; i < parseInt(ligne.quantite_recue, 10); i++) {
        const codeBarre = generateCodeBarreItem();
        const isSupivi = art.mode_gestion === 'SUIVI';
        // Pour contenance variable : utiliser la longueur saisie pour ce rouleau précis
        const qteItem = isSupivi
          ? (art.contenance_variable
              ? (parseFloat(ligne.quantites_par_item?.[i]) || art.contenance || null)
              : (art.contenance || null))
          : null;
        const { data: item } = await supabase
          .from('stock_items')
          .insert([{
            article_id:         ligne.article_id,
            reception_ligne_id: rl.id,
            code_barre:         codeBarre,
            statut:             'en_stock',
            quantite_initiale:  qteItem,
            quantite_restante:  qteItem,
            perte_finale:       0,
            operateur_id:       getOperateurId()
          }])
          .select().single();

        if (item) {
          // 4. Journaliser l'entrée
          await supabase.from('stock_mouvements').insert([{
            item_id:        item.id,
            article_id:     ligne.article_id,
            reception_id:   reception.id,
            operateur_id:   getOperateurId(),
            type_mouvement: 'entree_stock',
            quantite:       qteItem || 1,
            quantite_avant: 0,
            quantite_apres: qteItem || 1,
            notes: `Réception BL ${blForm.numero_bl || reception.id}`
          }]);
          allCreatedItems.push({ ...item, article: art });
        }
      }
    }

    setSavingBL(false);
    setShowBLModal(false);

    // 5. Impression des étiquettes
    if (allCreatedItems.length > 0) printLabels(allCreatedItems, blForm);

    // Reset
    setBlForm({ fournisseur_id: '', date_reception: todayStr(), numero_bl: '', numero_facture: '', notes: '' });
    setBlLignes([{ article_id: '', quantite_recue: 1, prix_unitaire_ht: '', quantites_par_item: [] }]);
    loadBase();
  };

  // ---- Impression des étiquettes ----
  const printLabels = (items, blHeader) => {
    const labelHtml = items.map(item => {
      const canvas = document.createElement('canvas');
      try {
        JsBarcode(canvas, item.code_barre, { format: 'CODE128', width: 2, height: 50, displayValue: false, margin: 0 });
      } catch (e) { return ''; }
      const barcodeUrl = canvas.toDataURL('image/png');
      const art = item.article;
      const infoLigne = art.mode_gestion === 'SUIVI' && art.contenance
        ? `${art.contenance} ${art.unite_consommation || ''}${art.laize_cm ? ` · L${art.laize_cm}cm` : ''}`
        : art.unite_stock;
      return `
        <div style="display:inline-block;border:1px solid #ccc;border-radius:4px;padding:8px 10px;margin:5px;width:180px;text-align:center;font-family:Arial,sans-serif;box-sizing:border-box;page-break-inside:avoid;">
          <div style="font-size:9px;color:#666;margin-bottom:2px;">${art.reference}</div>
          <div style="font-size:11px;font-weight:bold;margin-bottom:4px;line-height:1.2;">${art.designation}</div>
          <img src="${barcodeUrl}" style="width:100%;max-width:160px;" />
          <div style="font-size:10px;font-family:monospace;margin-top:2px;color:#333;">${item.code_barre}</div>
          <div style="font-size:9px;color:#555;margin-top:3px;">${infoLigne}</div>
          <div style="font-size:8px;color:#999;margin-top:2px;">${blHeader.date_reception}${blHeader.numero_bl ? ' · ' + blHeader.numero_bl : ''}</div>
        </div>`;
    }).join('');

    const win = window.open('', '', 'width=900,height=700');
    win.document.write(`<!DOCTYPE html><html><head><title>Étiquettes stock</title>
      <style>@media print { body { margin: 0; } } body { font-family: Arial; padding: 10px; }</style>
      </head><body>
      <div style="margin-bottom:12px;font-size:13px;color:#666;">
        ${items.length} étiquette${items.length > 1 ? 's' : ''} — BL ${blHeader.numero_bl || '—'} du ${blHeader.date_reception}
      </div>
      ${labelHtml}
      </body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 600);
  };

  // ---- Réimprimer l'étiquette d'un item de la vue "En Atelier" ----
  const reprintLabel = (item) => {
    printLabels([{ ...item, article: item.stock_articles }],
      { date_reception: '', numero_bl: '' });
  };

  // ---- Scan : rechercher un item ----
  const handleScanSearch = async () => {
    if (!scanInput.trim()) return;
    setScanError(''); setScanSuccess(''); setScannedItem(null); setScanQte(''); setScanNotes('');
    const { data: item, error } = await supabase
      .from('stock_items')
      .select('*, stock_articles(id, designation, reference, mode_gestion, unite_stock, unite_consommation, contenance, laize_cm)')
      .eq('code_barre', scanInput.trim())
      .single();
    if (error || !item) {
      setScanError(`Aucun item trouvé pour le code : ${scanInput.trim()}`);
      return;
    }
    if (item.statut === 'epuise' || item.statut === 'consomme') {
      setScanError(`Cet item est déjà ${item.statut === 'epuise' ? 'épuisé' : 'consommé'}.`);
      return;
    }
    setScannedItem(item);
  };

  // ---- Scan : exécuter l'action ----
  const handleScanAction = async (action) => {
    if (!scannedItem) return;
    setScanProcessing(true); setScanError('');
    const operateurId = getOperateurId();
    const art = scannedItem.stock_articles;

    if (action === 'sortie_atelier') {
      // SUIVI : passer en_stock → en_atelier
      await supabase.from('stock_items').update({
        statut: 'en_atelier',
        date_sortie_stock: new Date().toISOString(),
        operateur_id: operateurId
      }).eq('id', scannedItem.id);
      await supabase.from('stock_mouvements').insert([{
        item_id: scannedItem.id, article_id: art.id, operateur_id: operateurId,
        type_mouvement: 'sortie_atelier', quantite: scannedItem.quantite_restante,
        quantite_avant: scannedItem.quantite_restante, quantite_apres: scannedItem.quantite_restante,
        notes: scanNotes || null
      }]);
      setScanSuccess(`✅ ${art.designation} (${scannedItem.code_barre}) sorti vers l'atelier.`);
    }

    else if (action === 'consommation') {
      const qte = parseFloat(scanQte);
      if (!qte || qte <= 0) { setScanError('Saisissez une quantité valide.'); setScanProcessing(false); return; }
      if (qte > scannedItem.quantite_restante) { setScanError(`Quantité supérieure au restant (${scannedItem.quantite_restante} ${art.unite_consommation}).`); setScanProcessing(false); return; }
      const newQte = parseFloat((scannedItem.quantite_restante - qte).toFixed(3));
      await supabase.from('stock_items').update({
        quantite_restante: newQte,
        operateur_id: operateurId
      }).eq('id', scannedItem.id);
      await supabase.from('stock_mouvements').insert([{
        item_id: scannedItem.id, article_id: art.id, operateur_id: operateurId,
        type_mouvement: 'consommation', quantite: qte,
        quantite_avant: scannedItem.quantite_restante, quantite_apres: newQte,
        notes: scanNotes || null
      }]);
      setScanSuccess(`✅ ${qte} ${art.unite_consommation} consommés — reste ${newQte} ${art.unite_consommation}.`);
    }

    else if (action === 'declaration_fin') {
      const perte = parseFloat((scannedItem.quantite_restante || 0).toFixed(3));
      await supabase.from('stock_items').update({
        statut: 'epuise', quantite_restante: 0, perte_finale: perte,
        date_consommation: new Date().toISOString(), operateur_id: operateurId
      }).eq('id', scannedItem.id);
      await supabase.from('stock_mouvements').insert([{
        item_id: scannedItem.id, article_id: art.id, operateur_id: operateurId,
        type_mouvement: 'declaration_fin', quantite: perte,
        quantite_avant: scannedItem.quantite_restante, quantite_apres: 0,
        notes: perte > 0 ? `Perte : ${perte} ${art.unite_consommation}` : 'Épuisé naturellement'
      }]);
      setScanSuccess(`✅ ${art.designation} déclaré épuisé.${perte > 0 ? ` Perte : ${perte} ${art.unite_consommation}.` : ''}`);
    }

    else if (action === 'sortie_stock') {
      // SORTIE : en_stock → consomme
      await supabase.from('stock_items').update({
        statut: 'consomme',
        date_consommation: new Date().toISOString(), operateur_id: operateurId
      }).eq('id', scannedItem.id);
      await supabase.from('stock_mouvements').insert([{
        item_id: scannedItem.id, article_id: art.id, operateur_id: operateurId,
        type_mouvement: 'sortie_stock', quantite: 1,
        quantite_avant: 1, quantite_apres: 0,
        notes: scanNotes || null
      }]);
      setScanSuccess(`✅ ${art.designation} sorti du stock.`);
    }

    setScannedItem(null); setScanInput(''); setScanQte(''); setScanNotes('');
    loadBase();
    setScanProcessing(false);
    setTimeout(() => { if (scanInputRef.current) scanInputRef.current.focus(); }, 100);
  };

  // ---- Actions En Atelier ----
  const openAtelierAction = (item) => {
    setSelectedItemAtelier(item);
    setAtelierQte(''); setAtelierNotes(''); setAtelierError('');
    setShowAtelierModal(true);
  };

  const handleAtelierConsommation = async () => {
    const qte = parseFloat(atelierQte);
    const art = selectedItemAtelier.stock_articles;
    if (!qte || qte <= 0) { setAtelierError('Saisissez une quantité valide.'); return; }
    if (qte > selectedItemAtelier.quantite_restante) { setAtelierError(`Supérieur au restant (${selectedItemAtelier.quantite_restante} ${art.unite_consommation}).`); return; }
    setAtelierProcessing(true);
    const newQte = parseFloat((selectedItemAtelier.quantite_restante - qte).toFixed(3));
    await supabase.from('stock_items').update({ quantite_restante: newQte, operateur_id: getOperateurId() }).eq('id', selectedItemAtelier.id);
    await supabase.from('stock_mouvements').insert([{
      item_id: selectedItemAtelier.id, article_id: art.id, operateur_id: getOperateurId(),
      type_mouvement: 'consommation', quantite: qte,
      quantite_avant: selectedItemAtelier.quantite_restante, quantite_apres: newQte,
      notes: atelierNotes || null
    }]);
    setShowAtelierModal(false);
    loadItemsAtelier(); loadBase();
    setAtelierProcessing(false);
  };

  const handleAtelierDeclarerFini = async () => {
    setAtelierProcessing(true);
    const art = selectedItemAtelier.stock_articles;
    const perte = parseFloat((selectedItemAtelier.quantite_restante || 0).toFixed(3));
    await supabase.from('stock_items').update({
      statut: 'epuise', quantite_restante: 0, perte_finale: perte,
      date_consommation: new Date().toISOString(), operateur_id: getOperateurId()
    }).eq('id', selectedItemAtelier.id);
    await supabase.from('stock_mouvements').insert([{
      item_id: selectedItemAtelier.id, article_id: art.id, operateur_id: getOperateurId(),
      type_mouvement: 'declaration_fin', quantite: perte,
      quantite_avant: selectedItemAtelier.quantite_restante, quantite_apres: 0,
      notes: perte > 0 ? `Perte : ${perte} ${art.unite_consommation}` : null
    }]);
    setShowAtelierModal(false);
    loadItemsAtelier(); loadBase();
    setAtelierProcessing(false);
  };

  // ---- Détail article : charger ses items ----
  const openDetailModal = async (stockRow) => {
    setSelectedArticleDetail(stockRow);
    setFilterItemStatut('actifs');
    setShowDetailModal(true);
    setLoadingItems(true);
    const { data } = await supabase
      .from('stock_items')
      .select('*, stock_reception_lignes(reception_id, stock_receptions(date_reception, numero_bl))')
      .eq('article_id', stockRow.article_id)
      .order('created_at', { ascending: false });
    setArticleItems(data || []);
    setLoadingItems(false);
  };

  const reprintSingleItem = (item, stockRow) => {
    const art = {
      id: stockRow.article_id, reference: stockRow.reference,
      designation: stockRow.designation, mode_gestion: stockRow.mode_gestion,
      unite_stock: stockRow.unite_stock, unite_consommation: stockRow.unite_consommation,
      contenance: stockRow.contenance, laize_cm: stockRow.laize_cm
    };
    printLabels([{ ...item, article: art }], {
      date_reception: item.stock_reception_lignes?.stock_receptions?.date_reception || '',
      numero_bl: item.stock_reception_lignes?.stock_receptions?.numero_bl || ''
    });
  };

  // ---- Ajustement de stock ----
  const openAjustModal = (stockRow) => {
    setSelectedArticleAjust(stockRow);
    setAjustMode('add'); setAjustQte(''); setAjustQteInitiale('');
    setAjustMotif(''); setAjustError('');
    setShowAjustModal(true);
  };

  const handleAjustement = async () => {
    const n = parseInt(ajustQte, 10);
    if (!n || n <= 0) { setAjustError('Saisissez une quantité valide (> 0).'); return; }
    if (!ajustMotif.trim()) { setAjustError('Le motif est obligatoire.'); return; }
    const art = selectedArticleAjust;
    const operateurId = getOperateurId();
    setAjustProcessing(true); setAjustError('');

    if (ajustMode === 'add') {
      // Générer N nouveaux items
      const isSuivi = art.mode_gestion === 'SUIVI';
      const qteInit = isSuivi ? (parseFloat(ajustQteInitiale) || art.contenance || null) : null;
      for (let i = 0; i < n; i++) {
        const codeBarre = generateCodeBarreItem();
        const { data: item } = await supabase.from('stock_items').insert([{
          article_id: art.article_id,
          code_barre: codeBarre,
          statut: 'en_stock',
          quantite_initiale: qteInit,
          quantite_restante: qteInit,
          perte_finale: 0,
          operateur_id: operateurId
        }]).select().single();
        if (item) {
          await supabase.from('stock_mouvements').insert([{
            item_id: item.id, article_id: art.article_id, operateur_id: operateurId,
            type_mouvement: 'ajustement', quantite: qteInit || 1,
            quantite_avant: 0, quantite_apres: qteInit || 1,
            notes: `Ajustement +${n} — ${ajustMotif}`
          }]);
        }
      }
    } else {
      // Retirer N items (marquer en_stock → consomme)
      const { data: itemsARetirer } = await supabase
        .from('stock_items')
        .select('id, quantite_restante')
        .eq('article_id', art.article_id)
        .eq('statut', 'en_stock')
        .limit(n);
      if (!itemsARetirer || itemsARetirer.length < n) {
        setAjustError(`Seulement ${itemsARetirer?.length || 0} item(s) en stock disponibles.`);
        setAjustProcessing(false); return;
      }
      for (const item of itemsARetirer) {
        await supabase.from('stock_items').update({
          statut: 'consomme',
          date_consommation: new Date().toISOString(),
          operateur_id: operateurId
        }).eq('id', item.id);
        await supabase.from('stock_mouvements').insert([{
          item_id: item.id, article_id: art.article_id, operateur_id: operateurId,
          type_mouvement: 'ajustement', quantite: item.quantite_restante || 1,
          quantite_avant: item.quantite_restante || 1, quantite_apres: 0,
          notes: `Ajustement -${n} — ${ajustMotif}`
        }]);
      }
    }

    setShowAjustModal(false);
    loadBase();
    if (showDetailModal && selectedArticleDetail?.article_id === art.article_id) {
      openDetailModal(art);
    }
    setAjustProcessing(false);
  };

  // ---- Helpers affichage ----
  const statutStock = (s) => {
    if ((s.items_en_stock || 0) === 0)
      return { label: '🔴 Rupture', color: '#dc3545', bg: '#fff5f5' };
    if (s.seuil_alerte > 0 && (s.items_en_stock || 0) <= s.seuil_alerte)
      return { label: '🟠 Bas', color: '#fd7e14', bg: '#fff8f0' };
    return { label: '🟢 OK', color: '#1a7f4e', bg: '#f0fff4' };
  };

  const typeLabel = (t) => ({
    entree_stock:    { l: 'Réception',     c: '#1a7f4e', bg: '#d4edda' },
    sortie_atelier:  { l: 'Sortie atelier', c: '#1a5276', bg: '#d6eaf8' },
    consommation:    { l: 'Consommation',  c: '#856404', bg: '#fff3cd' },
    declaration_fin: { l: 'Fin / Épuisé',  c: '#dc3545', bg: '#f8d7da' },
    sortie_stock:    { l: 'Sortie stock',  c: '#5d6d7e', bg: '#e9ecef' },
    ajustement:      { l: 'Ajustement',    c: '#6f42c1', bg: '#e8d5ff' }
  }[t] || { l: t, c: '#6c757d', bg: '#e9ecef' });

  const filteredStock = stockData.filter(s => {
    const matchSearch = !searchStock || s.designation?.toLowerCase().includes(searchStock.toLowerCase()) || s.reference?.toLowerCase().includes(searchStock.toLowerCase());
    if (filterStatutStock === 'rupture') return matchSearch && (s.items_en_stock || 0) === 0;
    if (filterStatutStock === 'bas')     return matchSearch && s.seuil_alerte > 0 && (s.items_en_stock || 0) > 0 && (s.items_en_stock || 0) <= s.seuil_alerte;
    if (filterStatutStock === 'ok')      return matchSearch && (s.seuil_alerte === 0 || (s.items_en_stock || 0) > s.seuil_alerte);
    return matchSearch;
  });

  const views = [
    { id: 'vue_stock',  label: '📊 Vue Stock'   },
    { id: 'receptions', label: '📥 Réceptions'  },
    { id: 'en_atelier', label: '🔧 En Atelier'  },
    { id: 'mouvements', label: '📋 Mouvements'  }
  ];

  // ================================================================
  // RENDU PRINCIPAL
  // ================================================================
  return (
    <div>
      {/* Navigation interne */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {views.map(v => (
          <button key={v.id} onClick={() => setActiveView(v.id)} style={{
            padding: '8px 20px', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '13px',
            fontWeight: activeView === v.id ? '700' : '500',
            backgroundColor: activeView === v.id ? '#1aabf9' : '#f0f2f5',
            color: activeView === v.id ? '#fff' : '#495057',
            transition: 'all 0.2s'
          }}>{v.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button style={{ ...styles.btnPrimary, padding: '8px 18px', fontSize: '13px' }}
          onClick={() => { setShowScanModal(true); setTimeout(() => scanInputRef.current?.focus(), 100); }}>
          📷 Scanner
        </button>
        <button style={{ ...styles.btnPrimary, padding: '8px 18px', fontSize: '13px', backgroundColor: '#1a7f4e' }}
          onClick={() => { setShowBLModal(true); setErrorBL(''); }}>
          + Nouveau BL
        </button>
      </div>

      {/* ============================================================ */}
      {/* VUE STOCK                                                      */}
      {/* ============================================================ */}
      {activeView === 'vue_stock' && (
        <div>
          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            {[
              { label: 'Ruptures',         val: kpis.ruptures,        color: '#dc3545', bg: '#fff5f5', icon: '🔴' },
              { label: 'Sous seuil',       val: kpis.sousSeuilOu,     color: '#fd7e14', bg: '#fff8f0', icon: '🟠' },
              { label: 'Rouleaux en atelier', val: kpis.enAtelier,    color: '#1a5276', bg: '#d6eaf8', icon: '🔧' },
              { label: 'Réceptions ce mois', val: kpis.receptionsMonth, color: '#1a7f4e', bg: '#d4edda', icon: '📥' }
            ].map(k => (
              <div key={k.label} style={{ ...styles.card, backgroundColor: k.bg, border: 'none', padding: '18px 20px' }}>
                <div style={{ fontSize: '24px', marginBottom: '6px' }}>{k.icon}</div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: k.color }}>{k.val}</div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* Filtres */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <input type="text" placeholder="Rechercher..." value={searchStock} onChange={e => setSearchStock(e.target.value)} style={{ ...styles.input, width: '240px' }} />
            <select value={filterStatutStock} onChange={e => setFilterStatutStock(e.target.value)} style={{ ...styles.input, width: '160px' }}>
              <option value="tous">Tous les statuts</option>
              <option value="rupture">🔴 Rupture</option>
              <option value="bas">🟠 Sous seuil</option>
              <option value="ok">🟢 OK</option>
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Chargement...</div>
          ) : filteredStock.length === 0 ? (
            <div style={{ ...styles.card, textAlign: 'center', padding: '50px', color: '#6c757d' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📊</div>
              <div style={{ fontWeight: '600' }}>Aucun article en stock</div>
              <div style={{ fontSize: '13px', marginTop: '6px' }}>Créez des marchandises et réceptionnez un bon de livraison pour alimenter le stock.</div>
            </div>
          ) : (
            <div style={styles.card}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e8ecf1' }}>
                      {['Article', 'Mode', 'En stock', 'En atelier', 'Seuil', 'Statut', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStock.map((s, i) => {
                      const st = statutStock(s);
                      return (
                        <tr key={s.article_id || i} style={{ borderBottom: '1px solid #f0f2f5', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontWeight: '600', color: '#1a2332' }}>{s.designation}</div>
                            <div style={{ fontSize: '11px', color: '#6c757d' }}>{s.reference}{s.fournisseur ? ` · ${s.fournisseur}` : ''}</div>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {s.mode_gestion === 'SUIVI'
                              ? <span style={styles.badge('#1a5276', '#d6eaf8')}>SUIVI</span>
                              : <span style={styles.badge('#5d6d7e', '#eaecee')}>SORTIE</span>}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontWeight: '600', color: '#1a2332' }}>{s.items_en_stock || 0} {s.unite_stock}</div>
                            {s.mode_gestion === 'SUIVI' && (s.quantite_en_stock || 0) > 0 && (
                              <div style={{ fontSize: '11px', color: '#6c757d' }}>{s.quantite_en_stock} {s.unite_consommation}</div>
                            )}
                            {s.mode_gestion === 'SUIVI' && s.contenance_variable && (
                              <div style={{ fontSize: '10px', color: '#856404', fontWeight: '600' }}>📏 contenance variable</div>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#495057' }}>
                            {(s.items_en_atelier || 0) > 0
                              ? <><span style={{ fontWeight: '600' }}>{s.items_en_atelier}</span> {s.unite_stock}</>
                              : <span style={{ color: '#ccc' }}>—</span>}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#6c757d' }}>
                            {s.seuil_alerte > 0 ? s.seuil_alerte : <span style={{ color: '#ccc' }}>—</span>}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ ...styles.badge(st.color, st.bg), whiteSpace: 'nowrap' }}>{st.label}</span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button onClick={() => openDetailModal(s)} style={{ ...styles.btnSecondary, padding: '5px 10px', fontSize: '12px' }} title="Voir les items">🔍 Détail</button>
                              <button onClick={() => openAjustModal(s)} style={{ ...styles.btnSecondary, padding: '5px 10px', fontSize: '12px' }} title="Ajuster le stock">⚖️ Ajuster</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* RÉCEPTIONS                                                     */}
      {/* ============================================================ */}
      {activeView === 'receptions' && (
        <div>
          {receptions.length === 0 ? (
            <div style={{ ...styles.card, textAlign: 'center', padding: '50px', color: '#6c757d' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📥</div>
              <div style={{ fontWeight: '600' }}>Aucun bon de réception</div>
              <div style={{ fontSize: '13px', marginTop: '6px' }}>Cliquez sur "+ Nouveau BL" pour enregistrer votre première réception.</div>
            </div>
          ) : (
            <div style={styles.card}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e8ecf1' }}>
                      {['Date', 'Fournisseur', 'N° BL', 'N° Facture', 'Articles réceptionnés', 'Statut'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {receptions.map((r, i) => (
                      <tr key={r.id} style={{ borderBottom: '1px solid #f0f2f5', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ padding: '12px 14px', fontWeight: '500', whiteSpace: 'nowrap' }}>{r.date_reception}</td>
                        <td style={{ padding: '12px 14px', color: '#495057' }}>{r.stock_fournisseurs?.raison_sociale || '—'}</td>
                        <td style={{ padding: '12px 14px' }}>
                          {r.numero_bl ? <code style={{ backgroundColor: '#f0f2f5', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>{r.numero_bl}</code> : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: '13px', color: '#6c757d' }}>{r.numero_facture || '—'}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {(r.stock_reception_lignes || []).map((l, j) => (
                              <div key={j} style={{ fontSize: '12px', color: '#495057' }}>
                                {l.stock_articles?.designation} <span style={{ color: '#6c757d' }}>× {l.quantite_recue}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={styles.badge('#1a7f4e', '#d4edda')}>Validé</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* EN ATELIER                                                     */}
      {/* ============================================================ */}
      {activeView === 'en_atelier' && (
        <div>
          {itemsAtelier.length === 0 ? (
            <div style={{ ...styles.card, textAlign: 'center', padding: '50px', color: '#6c757d' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔧</div>
              <div style={{ fontWeight: '600' }}>Aucun article en atelier</div>
              <div style={{ fontSize: '13px', marginTop: '6px' }}>Les rouleaux et articles SUIVI sortis du stock apparaîtront ici.</div>
            </div>
          ) : (
            <div style={styles.card}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e8ecf1' }}>
                      {['Article', 'Code barre', 'Restant', 'Utilisé', 'Depuis', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {itemsAtelier.map((item, i) => {
                      const art = item.stock_articles;
                      const utilise = art?.contenance && item.quantite_restante != null
                        ? parseFloat((art.contenance - item.quantite_restante).toFixed(3)) : null;
                      const pctRestant = art?.contenance && item.quantite_restante != null
                        ? Math.round((item.quantite_restante / art.contenance) * 100) : null;
                      const alerte = pctRestant !== null && pctRestant < 20;
                      return (
                        <tr key={item.id} style={{ borderBottom: '1px solid #f0f2f5', backgroundColor: alerte ? '#fffdf0' : (i % 2 === 0 ? '#fff' : '#fafbfc') }}>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ fontWeight: '600', color: '#1a2332' }}>{art?.designation}</div>
                            <div style={{ fontSize: '11px', color: '#6c757d' }}>{art?.reference}</div>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <code style={{ backgroundColor: '#f0f2f5', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>{item.code_barre}</code>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            {item.quantite_restante != null ? (
                              <div>
                                <span style={{ fontWeight: '700', color: alerte ? '#fd7e14' : '#1a2332' }}>{item.quantite_restante}</span>
                                <span style={{ color: '#6c757d', fontSize: '12px' }}> {art?.unite_consommation}</span>
                                {pctRestant !== null && (
                                  <div style={{ marginTop: '3px', height: '4px', backgroundColor: '#e8ecf1', borderRadius: '2px', width: '80px' }}>
                                    <div style={{ height: '100%', borderRadius: '2px', width: `${pctRestant}%`, backgroundColor: alerte ? '#fd7e14' : '#1aabf9', transition: 'width 0.3s' }} />
                                  </div>
                                )}
                              </div>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#6c757d', fontSize: '13px' }}>
                            {utilise !== null ? `${utilise} ${art?.unite_consommation}` : '—'}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#6c757d', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {item.date_sortie_stock ? new Date(item.date_sortie_stock).toLocaleDateString('fr-FR') : '—'}
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button onClick={() => openAtelierAction(item)} style={{ ...styles.btnPrimary, padding: '6px 12px', fontSize: '12px' }}>Enregistrer conso</button>
                              <button onClick={() => reprintLabel(item)} style={{ ...styles.btnSecondary, padding: '6px 12px', fontSize: '12px' }}>🖨️</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* MOUVEMENTS                                                     */}
      {/* ============================================================ */}
      {activeView === 'mouvements' && (
        <div style={styles.card}>
          {mouvements.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>Aucun mouvement enregistré.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e8ecf1' }}>
                    {['Date & Heure', 'Article', 'Code barre', 'Type', 'Quantité', 'Opérateur', 'Notes'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '600', color: '#495057', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mouvements.map((m, i) => {
                    const tl = typeLabel(m.type_mouvement);
                    return (
                      <tr key={m.id} style={{ borderBottom: '1px solid #f0f2f5', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: '#6c757d', fontSize: '12px' }}>
                          {new Date(m.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ fontWeight: '500', color: '#1a2332' }}>{m.stock_articles?.designation || '—'}</div>
                          <div style={{ fontSize: '11px', color: '#6c757d' }}>{m.stock_articles?.reference}</div>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {m.stock_items?.code_barre
                            ? <code style={{ backgroundColor: '#f0f2f5', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{m.stock_items.code_barre}</code>
                            : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={styles.badge(tl.c, tl.bg)}>{tl.l}</span>
                        </td>
                        <td style={{ padding: '10px 14px', fontWeight: '500', color: '#1a2332' }}>
                          {m.quantite != null ? m.quantite : '—'}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#6c757d' }}>
                          {m.operateurs ? `${m.operateurs.prenom} ${m.operateurs.nom}` : '—'}
                        </td>
                        <td style={{ padding: '10px 14px', fontSize: '12px', color: '#6c757d' }}>{m.notes || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* MODAL : Nouveau bon de réception (BL)                            */}
      {/* ================================================================ */}
      {showBLModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '860px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1, borderBottom: '1px solid #e8ecf1', paddingBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1a2332' }}>📥 Nouveau bon de réception</h2>
              <button onClick={() => setShowBLModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6c757d' }}>×</button>
            </div>
            <div style={{ padding: '20px 28px 28px' }}>
              {errorBL && <div style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '12px 16px', color: '#dc3545', marginBottom: '16px', fontSize: '14px' }}>{errorBL}</div>}

              {/* En-tête BL */}
              <div style={{ marginBottom: '22px' }}>
                <div style={styles.sectionTitle}>En-tête du bon de réception</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={styles.label}>Fournisseur <span style={{ color: '#dc3545' }}>*</span></label>
                    <select style={styles.input} value={blForm.fournisseur_id} onChange={e => setBlForm(p => ({ ...p, fournisseur_id: e.target.value }))}>
                      <option value="">— Sélectionner —</option>
                      {fournisseurs.map(f => <option key={f.id} value={f.id}>{f.raison_sociale}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={styles.label}>Date de réception</label>
                    <input style={styles.input} type="date" value={blForm.date_reception} onChange={e => setBlForm(p => ({ ...p, date_reception: e.target.value }))} />
                  </div>
                  <div>
                    <label style={styles.label}>N° BL fournisseur</label>
                    <input style={styles.input} value={blForm.numero_bl} onChange={e => setBlForm(p => ({ ...p, numero_bl: e.target.value }))} placeholder="Ex: BL-2026-08847" />
                  </div>
                  <div>
                    <label style={styles.label}>N° Facture <span style={{ fontSize: '11px', color: '#6c757d' }}>(optionnel)</span></label>
                    <input style={styles.input} value={blForm.numero_facture} onChange={e => setBlForm(p => ({ ...p, numero_facture: e.target.value }))} placeholder="Ex: FA-2026-..." />
                  </div>
                  <div style={{ gridColumn: '2 / -1' }}>
                    <label style={styles.label}>Notes</label>
                    <input style={styles.input} value={blForm.notes} onChange={e => setBlForm(p => ({ ...p, notes: e.target.value }))} placeholder="Observations..." />
                  </div>
                </div>
              </div>

              {/* Lignes du BL */}
              <div style={{ marginBottom: '20px' }}>
                <div style={styles.sectionTitle}>Articles réceptionnés</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {blLignes.map((ligne, idx) => {
                    // Filtre : articles du fournisseur sélectionné (ou tous si aucun fournisseur)
                    const articlesFiltrés = blForm.fournisseur_id
                      ? articles.filter(a => a.fournisseur_id === blForm.fournisseur_id)
                      : articles;
                    const artSelectionne = articles.find(a => a.id === ligne.article_id);
                    const isContVar = artSelectionne?.mode_gestion === 'SUIVI' && artSelectionne?.contenance_variable;
                    return (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '3fr 1.2fr auto', gap: '10px', alignItems: 'end', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        {/* COL 1 : Article */}
                        <div>
                          <label style={{ ...styles.label, marginBottom: '4px' }}>Article</label>
                          <select style={styles.input} value={ligne.article_id}
                            onChange={e => {
                              const updated = [...blLignes];
                              const newArt = articles.find(a => a.id === e.target.value);
                              updated[idx] = {
                                ...updated[idx],
                                article_id:        e.target.value,
                                quantite_recue:    1,
                                quantites_par_item: newArt?.contenance_variable ? [''] : []
                              };
                              setBlLignes(updated);
                            }}>
                            <option value="">— Sélectionner un article {blForm.fournisseur_id ? '(ce fournisseur)' : ''} —</option>
                            {articlesFiltrés.map(a => <option key={a.id} value={a.id}>{a.designation} ({a.reference})</option>)}
                          </select>
                          {artSelectionne && (
                            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '3px' }}>
                              Mode {artSelectionne.mode_gestion}
                              {artSelectionne.mode_gestion === 'SUIVI' && artSelectionne.contenance && !artSelectionne.contenance_variable && ` · ${artSelectionne.contenance} ${artSelectionne.unite_consommation}/${artSelectionne.unite_stock}`}
                              {artSelectionne.contenance_variable && ' · 📏 longueur variable'}
                              {artSelectionne.prix_achat_ht && <span style={{ color: '#1a7f4e' }}> · {artSelectionne.prix_achat_ht} €/{artSelectionne.type_prix === 'PAR_UNITE_CONSO' ? artSelectionne.unite_consommation : artSelectionne.unite_stock}</span>}
                            </div>
                          )}
                          {blForm.fournisseur_id && articlesFiltrés.length === 0 && (
                            <div style={{ fontSize: '11px', color: '#fd7e14', marginTop: '3px' }}>Aucun article lié à ce fournisseur. Allez dans Marchandises pour associer des articles.</div>
                          )}
                        </div>

                        {/* COL 2 : Quantité ou Longueur (selon le type d'article) */}
                        {isContVar ? (
                          // Contenance variable : 1 ligne = 1 rouleau, saisir sa longueur
                          <div>
                            <label style={{ ...styles.label, marginBottom: '4px' }}>Longueur ({artSelectionne.unite_consommation})</label>
                            <input style={styles.input} type="number" min="0" step="0.1"
                              value={(ligne.quantites_par_item || [])[0] || ''}
                              onChange={e => {
                                const updated = [...blLignes];
                                updated[idx] = { ...updated[idx], quantite_recue: 1, quantites_par_item: [e.target.value] };
                                setBlLignes(updated);
                              }}
                              placeholder={artSelectionne.contenance ? String(artSelectionne.contenance) : 'Ex: 120'} />
                            <div style={{ fontSize: '11px', color: '#856404', marginTop: '3px', fontWeight: '600' }}>1 rouleau = 1 étiquette</div>
                          </div>
                        ) : (
                          // Contenance fixe ou SORTIE : nombre d'unités
                          <div>
                            <label style={{ ...styles.label, marginBottom: '4px' }}>Nb. de {artSelectionne?.unite_stock || 'articles'}</label>
                            <input style={styles.input} type="number" min="1" value={ligne.quantite_recue}
                              onChange={e => {
                                const updated = [...blLignes];
                                updated[idx] = { ...updated[idx], quantite_recue: e.target.value, quantites_par_item: [] };
                                setBlLignes(updated);
                              }} />
                            {artSelectionne && <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '3px' }}>{ligne.quantite_recue} étiquette{parseInt(ligne.quantite_recue) > 1 ? 's' : ''}</div>}
                          </div>
                        )}

                        {/* COL 3 : Supprimer */}
                        <div>
                          <button onClick={() => setBlLignes(blLignes.filter((_, i) => i !== idx))}
                            disabled={blLignes.length === 1}
                            style={{ ...styles.btnDanger, height: '38px', marginTop: '0', opacity: blLignes.length === 1 ? 0.4 : 1 }}>✕</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  style={{ ...styles.btnSecondary, marginTop: '10px', fontSize: '13px' }}
                  onClick={() => setBlLignes([...blLignes, { article_id: '', quantite_recue: 1, prix_unitaire_ht: '' }])}>
                  + Ajouter un article (ou un rouleau supplémentaire)
                </button>
              </div>

              {/* Résumé */}
              <div style={{ backgroundColor: '#f0f8ff', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#1a5276' }}>
                {blLignes.filter(l => l.article_id && l.quantite_recue > 0).reduce((sum, l) => sum + parseInt(l.quantite_recue, 10), 0)} étiquette(s) seront générées et imprimées à la validation.
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button style={styles.btnSecondary} onClick={() => setShowBLModal(false)}>Annuler</button>
                <button style={{ ...styles.btnPrimary, backgroundColor: '#1a7f4e' }} onClick={handleCreateBL} disabled={savingBL}>
                  {savingBL ? 'Création en cours...' : '✅ Valider & Imprimer les étiquettes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* MODAL : Scanner un barcode                                        */}
      {/* ================================================================ */}
      {showScanModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '540px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1a2332' }}>📷 Scanner un article</h2>
              <button onClick={() => { setShowScanModal(false); setScannedItem(null); setScanInput(''); setScanError(''); setScanSuccess(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6c757d' }}>×</button>
            </div>
            <div style={{ padding: '20px 28px 28px' }}>

              {/* Zone de saisie du code barre */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.label}>Code barre (scanner ou saisir manuellement)</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    ref={scanInputRef}
                    style={{ ...styles.input, flex: 1, fontSize: '16px', fontFamily: 'monospace' }}
                    value={scanInput}
                    onChange={e => { setScanInput(e.target.value); setScannedItem(null); setScanError(''); setScanSuccess(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleScanSearch()}
                    placeholder="Flasher ou taper le code..."
                    autoComplete="off"
                  />
                  <button style={styles.btnPrimary} onClick={handleScanSearch}>Rechercher</button>
                </div>
              </div>

              {/* Erreur */}
              {scanError && <div style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '12px 16px', color: '#dc3545', marginBottom: '16px', fontSize: '14px' }}>{scanError}</div>}

              {/* Succès */}
              {scanSuccess && (
                <div style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '8px', padding: '12px 16px', color: '#1a7f4e', marginBottom: '16px', fontSize: '14px' }}>
                  {scanSuccess}
                </div>
              )}

              {/* Item identifié → actions */}
              {scannedItem && !scanSuccess && (
                <div style={{ border: '2px solid #1aabf9', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                  {/* Info item */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ fontWeight: '700', fontSize: '16px', color: '#1a2332' }}>{scannedItem.stock_articles?.designation}</div>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                      {scannedItem.code_barre} · Mode {scannedItem.stock_articles?.mode_gestion}
                      {scannedItem.quantite_restante != null ? ` · Restant : ${scannedItem.quantite_restante} ${scannedItem.stock_articles?.unite_consommation}` : ''}
                    </div>
                    <div style={{ marginTop: '6px' }}>
                      {scannedItem.statut === 'en_stock'   && <span style={styles.badge('#1a7f4e', '#d4edda')}>En stock</span>}
                      {scannedItem.statut === 'en_atelier' && <span style={styles.badge('#1a5276', '#d6eaf8')}>En atelier</span>}
                    </div>
                  </div>

                  {/* Actions SUIVI en_stock */}
                  {scannedItem.stock_articles?.mode_gestion === 'SUIVI' && scannedItem.statut === 'en_stock' && (
                    <div>
                      <div style={{ fontSize: '13px', color: '#495057', marginBottom: '10px' }}>Cet item est en stock. Que voulez-vous faire ?</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={{ ...styles.btnPrimary, flex: 1 }} onClick={() => handleScanAction('sortie_atelier')} disabled={scanProcessing}>
                          🔧 Sortir vers l'atelier
                        </button>
                      </div>
                      <div style={{ marginTop: '10px' }}>
                        <label style={styles.label}>Notes (optionnel)</label>
                        <input style={styles.input} value={scanNotes} onChange={e => setScanNotes(e.target.value)} placeholder="..." />
                      </div>
                    </div>
                  )}

                  {/* Actions SUIVI en_atelier */}
                  {scannedItem.stock_articles?.mode_gestion === 'SUIVI' && scannedItem.statut === 'en_atelier' && (
                    <div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={styles.label}>Quantité consommée ({scannedItem.stock_articles?.unite_consommation})</label>
                        <input style={{ ...styles.input, fontSize: '18px' }} type="number" min="0" step="0.1"
                          value={scanQte} onChange={e => setScanQte(e.target.value)}
                          placeholder={`Max : ${scannedItem.quantite_restante}`} />
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={styles.label}>Notes</label>
                        <input style={styles.input} value={scanNotes} onChange={e => setScanNotes(e.target.value)} placeholder="..." />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={{ ...styles.btnPrimary, flex: 1 }} onClick={() => handleScanAction('consommation')} disabled={scanProcessing || !scanQte}>
                          ✅ Enregistrer la consommation
                        </button>
                        <button style={{ ...styles.btnDanger, flex: 1 }} onClick={() => handleScanAction('declaration_fin')} disabled={scanProcessing}>
                          🏁 Déclarer fini
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Action SORTIE en_stock */}
                  {scannedItem.stock_articles?.mode_gestion === 'SORTIE' && scannedItem.statut === 'en_stock' && (
                    <div>
                      <div style={{ fontSize: '13px', color: '#495057', marginBottom: '10px' }}>Cet item va être sorti définitivement du stock.</div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={styles.label}>Notes (optionnel)</label>
                        <input style={styles.input} value={scanNotes} onChange={e => setScanNotes(e.target.value)} placeholder="..." />
                      </div>
                      <button style={{ ...styles.btnPrimary, width: '100%' }} onClick={() => handleScanAction('sortie_stock')} disabled={scanProcessing}>
                        ✅ Confirmer la sortie du stock
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button style={styles.btnSecondary} onClick={() => { setShowScanModal(false); setScannedItem(null); setScanInput(''); setScanError(''); setScanSuccess(''); }}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* MODAL : Action sur item En Atelier                               */}
      {/* ================================================================ */}
      {showAtelierModal && selectedItemAtelier && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', padding: '28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: '#1a2332' }}>🔧 Enregistrer une consommation</h2>
              <button onClick={() => setShowAtelierModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6c757d' }}>×</button>
            </div>

            <div style={{ backgroundColor: '#f0f8ff', borderRadius: '8px', padding: '12px 14px', marginBottom: '18px' }}>
              <div style={{ fontWeight: '700', color: '#1a2332' }}>{selectedItemAtelier.stock_articles?.designation}</div>
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                {selectedItemAtelier.code_barre} · Restant : <strong>{selectedItemAtelier.quantite_restante} {selectedItemAtelier.stock_articles?.unite_consommation}</strong>
              </div>
            </div>

            {atelierError && <div style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '10px 14px', color: '#dc3545', marginBottom: '14px', fontSize: '13px' }}>{atelierError}</div>}

            <div style={{ marginBottom: '14px' }}>
              <label style={styles.label}>Quantité consommée ({selectedItemAtelier.stock_articles?.unite_consommation})</label>
              <input style={{ ...styles.input, fontSize: '20px', textAlign: 'center' }} type="number" min="0" step="0.1"
                value={atelierQte} onChange={e => setAtelierQte(e.target.value)}
                placeholder={`Max : ${selectedItemAtelier.quantite_restante}`} autoFocus />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={styles.label}>Notes (optionnel)</label>
              <input style={styles.input} value={atelierNotes} onChange={e => setAtelierNotes(e.target.value)} placeholder="..." />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={{ ...styles.btnPrimary, flex: 1 }} onClick={handleAtelierConsommation} disabled={atelierProcessing || !atelierQte}>
                {atelierProcessing ? '...' : '✅ Enregistrer'}
              </button>
              <button style={{ ...styles.btnDanger, flex: 1 }} onClick={handleAtelierDeclarerFini} disabled={atelierProcessing}>
                {atelierProcessing ? '...' : '🏁 Déclarer fini'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* MODAL : Détail article — tous ses items physiques                 */}
      {/* ================================================================ */}
      {showDetailModal && selectedArticleDetail && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding: '24px 28px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1, borderBottom: '1px solid #e8ecf1', paddingBottom: '16px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', color: '#1a2332' }}>{selectedArticleDetail.designation}</h2>
                <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '3px' }}>
                  {selectedArticleDetail.reference} · Mode {selectedArticleDetail.mode_gestion}
                  {selectedArticleDetail.fournisseur ? ` · ${selectedArticleDetail.fournisseur}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button onClick={() => openAjustModal(selectedArticleDetail)} style={{ ...styles.btnSecondary, fontSize: '13px', padding: '7px 14px' }}>⚖️ Ajuster le stock</button>
                <button onClick={() => setShowDetailModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6c757d' }}>×</button>
              </div>
            </div>
            <div style={{ padding: '20px 28px 28px' }}>
              {/* Filtres */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                {[['actifs', 'Actifs (stock + atelier)'], ['en_stock', 'En stock'], ['en_atelier', 'En atelier'], ['epuise', 'Épuisés'], ['consomme', 'Consommés'], ['tous', 'Tous']].map(([val, label]) => (
                  <button key={val} onClick={() => setFilterItemStatut(val)} style={{ padding: '5px 14px', border: 'none', borderRadius: '16px', cursor: 'pointer', fontSize: '12px', fontWeight: filterItemStatut === val ? '700' : '500', backgroundColor: filterItemStatut === val ? '#1aabf9' : '#f0f2f5', color: filterItemStatut === val ? '#fff' : '#495057' }}>{label}</button>
                ))}
              </div>

              {loadingItems ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#6c757d' }}>Chargement...</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e8ecf1' }}>
                        {['Code barre', 'Statut', selectedArticleDetail.mode_gestion === 'SUIVI' ? 'Restant' : 'Contenu', 'Réception BL', 'Date création', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: '600', color: '#495057' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {articleItems
                        .filter(item => {
                          if (filterItemStatut === 'actifs') return item.statut === 'en_stock' || item.statut === 'en_atelier';
                          if (filterItemStatut === 'tous') return true;
                          return item.statut === filterItemStatut;
                        })
                        .map((item, i) => {
                          const statutColors = {
                            en_stock:   { c: '#1a7f4e', bg: '#d4edda', l: 'En stock'   },
                            en_atelier: { c: '#1a5276', bg: '#d6eaf8', l: 'En atelier' },
                            epuise:     { c: '#856404', bg: '#fff3cd', l: 'Épuisé'     },
                            consomme:   { c: '#6c757d', bg: '#e9ecef', l: 'Consommé'   }
                          };
                          const sc = statutColors[item.statut] || statutColors.consomme;
                          const bl = item.stock_reception_lignes?.stock_receptions;
                          return (
                            <tr key={item.id} style={{ borderBottom: '1px solid #f0f2f5', backgroundColor: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                              <td style={{ padding: '10px 12px' }}>
                                <code style={{ backgroundColor: '#f0f2f5', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>{item.code_barre}</code>
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                <span style={styles.badge(sc.c, sc.bg)}>{sc.l}</span>
                                {item.statut === 'epuise' && item.perte_finale > 0 && (
                                  <div style={{ fontSize: '11px', color: '#856404', marginTop: '2px' }}>Perte : {item.perte_finale} {selectedArticleDetail.unite_consommation}</div>
                                )}
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                {selectedArticleDetail.mode_gestion === 'SUIVI' ? (
                                  item.quantite_restante != null
                                    ? <span style={{ fontWeight: '600' }}>{item.quantite_restante} <span style={{ fontWeight: '400', color: '#6c757d', fontSize: '11px' }}>{selectedArticleDetail.unite_consommation}</span></span>
                                    : '—'
                                ) : (
                                  <span style={{ color: '#6c757d', fontSize: '12px' }}>{selectedArticleDetail.unite_stock}</span>
                                )}
                              </td>
                              <td style={{ padding: '10px 12px', fontSize: '12px', color: '#6c757d' }}>
                                {bl ? <><div>{bl.date_reception}</div><div style={{ fontSize: '11px' }}>{bl.numero_bl || '—'}</div></> : '—'}
                              </td>
                              <td style={{ padding: '10px 12px', fontSize: '12px', color: '#6c757d', whiteSpace: 'nowrap' }}>
                                {new Date(item.created_at).toLocaleDateString('fr-FR')}
                              </td>
                              <td style={{ padding: '10px 12px' }}>
                                {(item.statut === 'en_stock' || item.statut === 'en_atelier') && (
                                  <button onClick={() => reprintSingleItem(item, selectedArticleDetail)} style={{ ...styles.btnSecondary, padding: '5px 10px', fontSize: '12px' }} title="Réimprimer l'étiquette">🖨️ Étiquette</button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#6c757d', textAlign: 'right' }}>
                    {articleItems.filter(item => filterItemStatut === 'actifs' ? (item.statut === 'en_stock' || item.statut === 'en_atelier') : filterItemStatut === 'tous' ? true : item.statut === filterItemStatut).length} item(s) affiché(s)
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button style={styles.btnSecondary} onClick={() => setShowDetailModal(false)}>Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* MODAL : Ajustement de stock                                      */}
      {/* ================================================================ */}
      {showAjustModal && selectedArticleAjust && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', width: '100%', maxWidth: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', padding: '28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: '#1a2332' }}>⚖️ Ajustement de stock</h2>
              <button onClick={() => setShowAjustModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '22px', color: '#6c757d' }}>×</button>
            </div>

            {/* Info article */}
            <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px 14px', marginBottom: '18px' }}>
              <div style={{ fontWeight: '700', color: '#1a2332' }}>{selectedArticleAjust.designation}</div>
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                Stock actuel : <strong>{selectedArticleAjust.items_en_stock || 0} {selectedArticleAjust.unite_stock}</strong> en stock
                {(selectedArticleAjust.items_en_atelier || 0) > 0 && ` · ${selectedArticleAjust.items_en_atelier} en atelier`}
              </div>
            </div>

            {ajustError && <div style={{ backgroundColor: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '10px 14px', color: '#dc3545', marginBottom: '14px', fontSize: '13px' }}>{ajustError}</div>}

            {/* Mode : Entrée / Sortie */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '18px' }}>
              {[['add', '➕ Entrée stock', 'Ajouter des items (correction à la hausse, oubli de réception...)'],
                ['remove', '➖ Sortie stock', 'Retirer des items (casse, perte, erreur de comptage...)']]
                .map(([val, label, desc]) => (
                  <div key={val} onClick={() => { setAjustMode(val); setAjustError(''); }}
                    style={{ flex: 1, padding: '12px', borderRadius: '8px', cursor: 'pointer', border: ajustMode === val ? '2px solid #1aabf9' : '2px solid #e8ecf1', backgroundColor: ajustMode === val ? '#f0f8ff' : '#fafbfc' }}>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: ajustMode === val ? '#1aabf9' : '#495057' }}>{label}</div>
                    <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '3px', lineHeight: '1.4' }}>{desc}</div>
                  </div>
                ))}
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={styles.label}>Quantité ({selectedArticleAjust.unite_stock}) <span style={{ color: '#dc3545' }}>*</span></label>
              <input style={{ ...styles.input, fontSize: '18px', textAlign: 'center' }} type="number" min="1"
                value={ajustQte} onChange={e => setAjustQte(e.target.value)} placeholder="Ex: 5" autoFocus />
            </div>

            {/* Pour SUIVI en entrée : demander la quantité initiale par item */}
            {ajustMode === 'add' && selectedArticleAjust.mode_gestion === 'SUIVI' && (
              <div style={{ marginBottom: '14px' }}>
                <label style={styles.label}>
                  Quantité initiale par {selectedArticleAjust.unite_stock} ({selectedArticleAjust.unite_consommation})
                  <span style={{ fontSize: '11px', color: '#6c757d', marginLeft: '6px' }}>Laisser vide pour utiliser la valeur catalogue ({selectedArticleAjust.contenance})</span>
                </label>
                <input style={styles.input} type="number" min="0" step="0.1"
                  value={ajustQteInitiale} onChange={e => setAjustQteInitiale(e.target.value)}
                  placeholder={selectedArticleAjust.contenance ? String(selectedArticleAjust.contenance) : 'Ex: 120'} />
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={styles.label}>Motif <span style={{ color: '#dc3545' }}>*</span></label>
              <input style={styles.input} value={ajustMotif} onChange={e => setAjustMotif(e.target.value)}
                placeholder="Ex: Inventaire physique du 13/07, Oubli de réception, Casse..." />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={styles.btnSecondary} onClick={() => setShowAjustModal(false)}>Annuler</button>
              <button style={{ ...styles.btnPrimary, flex: 1, backgroundColor: ajustMode === 'add' ? '#1a7f4e' : '#dc3545' }}
                onClick={handleAjustement} disabled={ajustProcessing}>
                {ajustProcessing ? 'Traitement...' : (ajustMode === 'add' ? `➕ Ajouter ${ajustQte || '?'} ${selectedArticleAjust.unite_stock}` : `➖ Retirer ${ajustQte || '?'} ${selectedArticleAjust.unite_stock}`)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionStock;

